import { getPublicReservationSettings } from '../../../core/services/publicReservationSettingsService';
import ReservationGroupNotice from './ReservationGroupNotice';
import { reservationGroupContactReason, reservationGroupSummary } from '../../../core/reservations/reservationGroupPolicy';
import { reservationCalendarDayBlockReason, reservationTimeLimitReason, reservationTimeLimitMessage } from '../../../core/reservations/reservationTimeLimits';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Unit,
  ReservationSetting,
  ReservationCapacity,
  GuestFormSettings,
  CustomSelectField,
} from '../../../core/models/data';
import { db, Timestamp } from '../../../core/firebase/config';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
} from 'firebase/firestore';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import CalendarIcon from '../../../../components/icons/CalendarIcon';
import CopyIcon from '../../../../components/icons/CopyIcon';
import { translations } from '../../../lib/i18n';
import {
  ReservationThemeTokens,
  buildReservationTheme,
  defaultThemeSettings,
  syncThemeCssVariables,
} from '../../../core/ui/reservationTheme';
import ReservationVenueMap from './ReservationVenueMap';
import PublicReservationLayout from './PublicReservationLayout';
import {
  loadThemeStudioActiveTheme,
  resolveThemeStudioRuntimeTheme,
  type ThemeStudioActiveTheme,
} from './themeStudioActiveThemeRuntime';
import { normalizeServiceOpeningHours } from '../../../core/reservations/serviceOpeningHours';
import {
  addMinutesToTime,
  isReservationTimeClosed,
  normalizeBookingClosures,
} from '../../../core/reservations/bookingClosurePolicy';
import {
  formatPublicServiceOpeningDays,
  isReservationFormSubmittable,
  isValidReservationTime,
  resolveCustomSelectOptionLabel,
  resolvePublicLabel,
  resolvePublicName,
} from '../../../core/reservations/publicReservationTranslations';
import {
  isRetryableReservationError,
  normalizeSubmitError,
  shouldShowReservationErrorDebugId,
  type SubmitError,
} from '../../utils/normalizeSubmitError';
import { getOnlineStatus } from '../../utils/getOnlineStatus';
import { v4 as uuidv4 } from 'uuid';
import { computeReservationBucketKeysClient } from '../../../core/utils/capacityBuckets';
import { guestCreateReservation } from '../../../core/services/guestReservationService';
import { reservationGraceMinutes, reservationTermsFingerprint } from '../../../core/reservations/reservationBusinessRules';
import { resolvePublicBookingBackgroundUrl } from '../../../core/reservations/publicBookingBackground';
import { buildPublicReservationTitle, buildPublicReservationWatermark, resolvePublicBusinessName } from './publicBusinessIdentity';
import { createReservationAnalyticsPublicCollectorWriter } from '../../../core/reservationAnalytics/analyticsPublicCollectorAdapter';
import { trackReservationAnalyticsEvent } from '../../../core/reservationAnalytics/analyticsCollector';
import { createBrowserBookingFlowAnalyticsEmitter } from '../../../core/reservationAnalytics/bookingFlowAnalyticsEmitter';
import { createBookingFlowAnalyticsPublicCollectorWriter } from '../../../core/reservationAnalytics/bookingFlowAnalyticsPublicCollectorAdapter';
import { requestBookingFlowAvailability } from '../../../core/reservationAnalytics/bookingFlowAvailabilityService';
import { resolveUnitTimezone } from '../../../core/location/unitLocation';
import {
  BOOKING_FLOW_AVAILABILITY_DEFAULT_DURATION_MINUTES,
  BOOKING_FLOW_AVAILABILITY_MAX_HEADCOUNT,
  resolveBookingFlowAvailabilityInterval,
  type BookingFlowAvailabilityRequest,
  type BookingFlowAvailabilityResponse,
} from '../../../core/reservationAnalytics/bookingFlowAvailabilityContract';
import type { TimeSelectionSource, ValidationBlockReason } from '../../../core/reservationAnalytics/bookingFlowAnalyticsTypes';
import { isControlledReservationRejection, resolveBookingFlowTransportFailureKind } from '../../../core/reservationAnalytics/bookingFlowAnalyticsSubmitOutcome';
import {
  createReservationDiagnosticInstanceId,
  recordRuntimeDiagnosticEvent,
  sanitizeError,
} from '../../diagnostics/runtimeDiagnostics';

type Locale = 'hu' | 'en';

// Theme Studio is a future release. Do not activate published draft tokens or
// assets on the customer-facing booking page until its public rollout is approved.
const PUBLIC_THEME_STUDIO_RUNTIME_ENABLED = false;

type AuthoritativeAvailabilityState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; response: BookingFlowAvailabilityResponse; durationMinutes: number; requestSequence: number; selectionKey: string }
  | { status: 'error' };

const PlayfulBubbles = () => (
  <>
    <div className="pointer-events-none absolute w-64 h-64 bg-white/40 blur-3xl rounded-full -top-10 -left-10" />
    <div className="pointer-events-none absolute w-52 h-52 bg-white/30 blur-2xl rounded-full top-20 right-10" />
    <div className="pointer-events-none absolute w-40 h-40 bg-white/25 blur-2xl rounded-full bottom-10 left-1/4" />
  </>
);

interface ReservationPageProps {
  unitId: string;
}

const toDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const resolveUnitBookingTimezone = (settings: ReservationSetting | null): string =>
  resolveUnitTimezone(null, settings);

const defaultReservationTheme = defaultThemeSettings;

const DEFAULT_GUEST_FORM: GuestFormSettings = {
  customSelects: [],
};

const inputTextStyle = { color: '#111827' as const };

const toRgba = (hex: string, alpha: number) => {
  const parsed = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!parsed) return `rgba(0,0,0,${alpha})`;
  const r = parseInt(parsed[1], 16);
  const g = parseInt(parsed[2], 16);
  const b = parseInt(parsed[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const resolveHeaderLogoUrl = (
  settings?: ReservationSetting | null,
  unit?: Unit | null,
  activeTheme?: ThemeStudioActiveTheme | null,
): string | null => {
  const activeHeaderLogo = activeTheme?.activeAssetSnapshot?.brandHeader?.headerLogo;
  const activeHeaderLogoMode = activeTheme?.activeAssetSnapshot?.brandHeader?.headerLogoMode?.mode;
  if (activeHeaderLogoMode === 'customLogo' && activeHeaderLogo?.source === 'themeStudio' && activeHeaderLogo.url) {
    return activeHeaderLogo.url;
  }
  if (activeHeaderLogoMode === 'unitLogo' && (unit?.logoUrl || unit?.logo)) {
    return (unit.logoUrl || unit.logo) ?? null;
  }

  const mode =
    settings?.theme?.headerLogoMode || settings?.theme?.timeWindowLogoMode || 'none';

  if (mode === 'custom') {
    if (settings?.theme?.headerLogoUrl) return settings.theme.headerLogoUrl;
    if (settings?.theme?.timeWindowLogoUrl) return settings.theme.timeWindowLogoUrl;
  }

  if (mode === 'unit' && (unit?.logoUrl || unit?.logo)) {
    return (unit.logoUrl || unit.logo) ?? null;
  }

  return null;
};


const firstExistingText = (...values: unknown[]): string | null => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
};

const resolveVenueTrustContact = (
  unit: Unit,
  settings: ReservationSetting,
): { address: string | null; phone: string | null } => {
  const unitData: Record<string, unknown> = { ...unit };
  const settingsData: Record<string, unknown> = { ...settings };
  return {
    address: firstExistingText(
      unitData.address,
      unitData.publicAddress,
      unitData.location,
      settingsData.address,
      settingsData.publicAddress,
      settingsData.location,
    ),
    phone: firstExistingText(
      unitData.phone,
      unitData.publicPhone,
      unitData.contactPhone,
      settingsData.phone,
      settingsData.publicPhone,
      settingsData.contactPhone,
    ),
  };
};

const VenueTrustCard: React.FC<{
  unit: Unit;
  settings: ReservationSetting;
  brandLogoUrl: string | null;
  themeProps: Pick<ReservationThemeTokens, 'colors' | 'radiusClass'>;
  t: typeof translations['hu'];
  locale: Locale;
}> = ({ settings, themeProps, locale }) => {
  const openingHours = normalizeServiceOpeningHours(settings)
    .filter(item => item.isEnabled)
    .flatMap(item => item.schedules.filter(schedule => schedule.days.length > 0 && /^([01]\d|2[0-3]):[0-5]\d$/.test(schedule.opensAt) && /^([01]\d|2[0-3]):[0-5]\d$/.test(schedule.closesAt))
      .map(schedule => `${resolvePublicName(item, locale)} ${formatPublicServiceOpeningDays(schedule.days, locale)}: ${schedule.opensAt}–${schedule.closesAt}`));
  return (
    <aside data-reservation-venue-trust-card className={`${themeProps.radiusClass} border p-3 md:p-4`}
      style={{ backgroundColor: toRgba(themeProps.colors.surface, 0.88), borderColor: toRgba(themeProps.colors.textSecondary, 0.16), color: themeProps.colors.textPrimary }}>
      <div className="grid gap-2 text-sm sm:grid-cols-2">
        {settings.bookableWindow && <div><strong>{locale === 'hu' ? 'Foglalási időablak' : 'Reservation hours'}</strong><p>{settings.bookableWindow.from}–{settings.bookableWindow.to}</p></div>}
        {openingHours.length > 0 && <div><strong>{locale === 'hu' ? 'Nyitvatartás' : 'Opening hours'}</strong><ul>{openingHours.map((hours, index) => <li key={index}>{hours}</li>)}</ul></div>}
      </div>
    </aside>
  );
};

const ProgressIndicator: React.FC<{
  currentStep: number;
  t: typeof translations['hu'];
  theme: ReservationThemeTokens;
  locale: Locale;
}> = ({ currentStep, t, theme, locale }) => {
  const steps = [t.step1, t.step2, t.step3];
  return (
    <div
      role="list"
      aria-label={locale === 'hu' ? 'Foglalási folyamat' : 'Reservation progress'}
      className={`w-full max-w-2xl mx-auto flex items-center justify-center ${theme.styles.stepWrapper}`.trim()}
    >
      <div className="relative">
        <div className="absolute inset-x-4 top-5 md:top-6" aria-hidden>
          <div className="grid grid-cols-2 gap-4 items-center">
            {[0, 1].map((segment) => {
              const segmentActive = currentStep > segment + 1;
              return (
                <div
                  key={`segment-${segment}`}
                  className="h-1 rounded-full w-full transition-colors duration-300 motion-reduce:transition-none"
                  style={{
                    backgroundColor: segmentActive
                      ? theme.colors.primary
                      : theme.colors.surface,
                    boxShadow: segmentActive
                      ? `0 0 0 1px ${theme.colors.primary}`
                      : `0 0 0 1px ${theme.colors.surface}`,
                  }}
                />
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-3 items-center gap-4 relative">
          {steps.map((label, index) => {
            const stepNumber = index + 1;
            const isCompleted = currentStep > stepNumber;
            const isActive = currentStep === stepNumber;
            return (
              <div
                key={stepNumber}
                role="listitem"
                aria-current={isActive ? 'step' : undefined}
                className="flex flex-col items-center text-center gap-2"
              >
                <div
                  aria-hidden="true"
                  className={`w-10 h-10 flex items-center justify-center font-bold transition-all duration-300 motion-reduce:transition-none ${
                    isCompleted
                      ? theme.styles.stepActive
                      : isActive
                      ? theme.styles.stepActive
                      : theme.styles.stepInactive
                  } ${theme.radiusClass}`}
                  style={{
                    backgroundColor: isCompleted
                      ? theme.colors.primary
                      : theme.colors.surface,
                    color: isCompleted
                      ? '#fff'
                      : isActive
                      ? theme.colors.primary
                      : theme.colors.textSecondary,
                    borderColor: isActive
                      ? theme.colors.primary
                      : theme.colors.surface,
                    boxShadow: isActive
                      ? `0 10px 30px ${toRgba(theme.colors.primary, 0.18)}`
                      : undefined,
                  }}
                >
                  {isCompleted ? '✓' : stepNumber}
                </div>
                <p
                  className={`text-sm font-semibold transition-colors leading-tight motion-reduce:transition-none ${theme.fontFamilyClass}`}
                  style={{
                    color: isActive
                      ? theme.colors.textPrimary
                      : theme.colors.textSecondary,
                  }}
                >
                  {label}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const buildPublicUnit = (
  unitId: string,
  settings: ReservationSetting | null
): Unit => {
  const settingsAny = settings as Record<string, any> | null;
  const name = resolvePublicBusinessName(settingsAny);
  const logoUrl =
    settings?.theme?.headerLogoUrl || settings?.theme?.timeWindowLogoUrl;

  return {
    id: unitId,
    name,
    logoUrl,
  };
};

type SeatingPreference = 'any' | 'bar' | 'table' | 'terrace';
const DEFAULT_SEATING_OPTIONS: Array<{ value: SeatingPreference; labelKey: keyof typeof translations['hu'] }> = [
  { value: 'any', labelKey: 'seatingPreferenceAny' },
  { value: 'bar', labelKey: 'seatingPreferenceBar' },
  { value: 'table', labelKey: 'seatingPreferenceTable' },
  { value: 'terrace', labelKey: 'seatingPreferenceOutdoor' },
];

const ReservationPage: React.FC<ReservationPageProps> = ({ unitId }) => {
  const [step, setStep] = useState(1);
  const [unit, setUnit] = useState<Unit | null>(null);
  const [settings, setSettings] = useState<ReservationSetting | null>(null);
  const [activeTheme, setActiveTheme] = useState<ThemeStudioActiveTheme | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitState, setSubmitState] = useState<
    'idle' | 'submitting' | 'success' | 'error'
  >('idle');
  const [submitError, setSubmitError] = useState<SubmitError | null>(null);
  const [locale, setLocale] = useState<Locale>('hu');

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    headcount: '2',
    startTime: '',
    endTime: '',
    phone: '',
    email: '',
    seatingPreference: 'any' as SeatingPreference,
    customData: {} as Record<string, string>,
  });
  const [submittedData, setSubmittedData] = useState<any>(null);
  const [graceAcceptance, setGraceAcceptance] = useState(false);
  const acceptanceGraceMinutes = settings ? reservationGraceMinutes(settings, Number(formData.headcount)) : null;
  const acceptanceTermsFingerprint = settings ? reservationTermsFingerprint(settings) : null;
  useEffect(() => {
    setGraceAcceptance(false);
  }, [acceptanceGraceMinutes, acceptanceTermsFingerprint]);

  const isSubmitting = submitState === 'submitting';

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dailyHeadcounts, setDailyHeadcounts] = useState<Map<string, number>>(
    new Map()
  );
  const [capacityByDate, setCapacityByDate] = useState<
    Map<string, ReservationCapacity>
  >(new Map());
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityLoadError, setAvailabilityLoadError] = useState(false);
  const [authoritativeAvailability, setAuthoritativeAvailability] = useState<AuthoritativeAvailabilityState>({ status: 'idle' });
  const availabilityRequestSequenceRef = useRef(0);
  const errorBannerRef = useRef<HTMLDivElement | null>(null);
  const stepRegionRef = useRef<HTMLDivElement | null>(null);
  const previousStepRef = useRef(step);
  const localeRef = useRef(locale);
  const defaultHeadcountSyncRef = useRef<string | null>(null);
  const bookingFlowReplayCredentialKeyRef = useRef<string | null>(null);
  const bookingFlowReplayCredentialMemoryRef = useRef<{ key: string; credential: string } | null>(null);
  const diagnosticInstanceIdRef = useRef<string | null>(null);
  if (!diagnosticInstanceIdRef.current) {
    diagnosticInstanceIdRef.current = createReservationDiagnosticInstanceId();
  }
  const diagnosticInstanceId = diagnosticInstanceIdRef.current;
  recordRuntimeDiagnosticEvent({
    event: 'render:start',
    componentInstanceId: diagnosticInstanceId,
    unitId,
  });
  const timeWindowCapacityValue =
    typeof settings?.timeWindowCapacity === 'number' &&
    Number.isFinite(settings.timeWindowCapacity) &&
    settings.timeWindowCapacity > 0
      ? settings.timeWindowCapacity
      : null;

  const theme = useMemo(() => {
    const persistedTheme = settings?.theme || defaultReservationTheme;
    // Independent live public background authority until Theme Studio rollout.
    // Explicitly clearing canonical image must not resurrect a retired asset.
    const liveThemeSettings = {
      ...persistedTheme,
      backgroundImageUrl: resolvePublicBookingBackgroundUrl(
        settings?.publicBackgroundImageUrl,
        persistedTheme.backgroundImageUrl,
      ),
    };
    const legacyTheme = buildReservationTheme(liveThemeSettings, settings?.uiTheme);
    if (!PUBLIC_THEME_STUDIO_RUNTIME_ENABLED) return legacyTheme;
    return resolveThemeStudioRuntimeTheme({
      activeTheme,
      legacyTheme,
      legacyThemeSettings: liveThemeSettings,
      legacyUiTheme: settings?.uiTheme,
    }).theme;
  }, [activeTheme, settings]);

  const isMinimalGlassTheme = theme.uiTheme === 'minimal_glass';

  useEffect(() => {
    recordRuntimeDiagnosticEvent({ event: 'commit:mounted', componentInstanceId: diagnosticInstanceId, unitId });
    console.log('[Analytics] ReservationPage mounted');
    return () => {
      recordRuntimeDiagnosticEvent({ event: 'cleanup:unmounted', componentInstanceId: diagnosticInstanceId, unitId });
    };
  }, []);

  console.log('[Analytics] Creating analytics writer');
  const analyticsWriter = useMemo(() => {
    try {
      recordRuntimeDiagnosticEvent({ event: 'writer:create:start', componentInstanceId: diagnosticInstanceId, unitId });
      const writer = createReservationAnalyticsPublicCollectorWriter();
      recordRuntimeDiagnosticEvent({ event: 'writer:create:end', componentInstanceId: diagnosticInstanceId, unitId });
      return writer;
    } catch (error) {
      const safe = sanitizeError(error);
      recordRuntimeDiagnosticEvent({ event: 'writer:create:throw', componentInstanceId: diagnosticInstanceId, unitId, errorName: safe.errorName, errorMessage: safe.errorMessage, isAbortLike: safe.isAbortLike });
      throw error;
    }
  }, []);
  console.log('[Analytics] Analytics writer created');

  const bookingTimezone = resolveUnitBookingTimezone(settings);
  const bookingFlowAnalyticsWriter = useMemo(() => createBookingFlowAnalyticsPublicCollectorWriter(), []);
  const bookingFlowAnalytics = useMemo(() => createBrowserBookingFlowAnalyticsEmitter({
    unitId,
    writer: bookingFlowAnalyticsWriter,
  }), [bookingFlowAnalyticsWriter, unitId]);

  const replayCredentialStorage = useMemo(() => ({
    key: (correlation: { sessionId: string; bookingFlowId: string; submitAttemptId: string }) =>
      `bookingFlowReplayCredential:${encodeURIComponent(unitId)}:${encodeURIComponent(correlation.sessionId)}:${encodeURIComponent(correlation.bookingFlowId)}:${encodeURIComponent(correlation.submitAttemptId)}`,
    getOrCreate: (correlation: { sessionId: string; bookingFlowId: string; submitAttemptId: string }) => {
      const key = replayCredentialStorage.key(correlation);
      if (bookingFlowReplayCredentialMemoryRef.current?.key === key) {
        return bookingFlowReplayCredentialMemoryRef.current.credential;
      }
      try {
        const existing = window.sessionStorage.getItem(key);
        if (existing) {
          bookingFlowReplayCredentialKeyRef.current = key;
          bookingFlowReplayCredentialMemoryRef.current = { key, credential: existing };
          return existing;
        }
      } catch {
        // Replay credential storage is best-effort.
      }
      const credential = uuidv4();
      bookingFlowReplayCredentialMemoryRef.current = { key, credential };
      try {
        if (bookingFlowReplayCredentialKeyRef.current && bookingFlowReplayCredentialKeyRef.current !== key) {
          window.sessionStorage.removeItem(bookingFlowReplayCredentialKeyRef.current);
        }
        window.sessionStorage.setItem(key, credential);
        bookingFlowReplayCredentialKeyRef.current = key;
      } catch {
        // Booking must not depend on replay credential persistence.
      }
      return credential;
    },
    clear: () => {
      try {
        if (bookingFlowReplayCredentialKeyRef.current) window.sessionStorage.removeItem(bookingFlowReplayCredentialKeyRef.current);
      } catch {
        // Best-effort cleanup.
      }
      bookingFlowReplayCredentialKeyRef.current = null;
      bookingFlowReplayCredentialMemoryRef.current = null;
    },
  }), [unitId]);

  useEffect(() => {
    if (!settings) return;
    recordRuntimeDiagnosticEvent({ event: 'effect:page_view', componentInstanceId: diagnosticInstanceId, unitId });
    console.log('[Analytics] page_view effect fired');
    console.log('[AnalyticsBoundaryProbe] before trackReservationAnalyticsEvent call', {
      type: typeof trackReservationAnalyticsEvent,
      callable: typeof trackReservationAnalyticsEvent === 'function',
      name: typeof trackReservationAnalyticsEvent === 'function' ? trackReservationAnalyticsEvent.name : null,
      length: typeof trackReservationAnalyticsEvent === 'function' ? trackReservationAnalyticsEvent.length : null,
      sourcePrefix: typeof trackReservationAnalyticsEvent === 'function'
        ? Function.prototype.toString.call(trackReservationAnalyticsEvent).slice(0, 200)
        : null,
    });
    try {
      console.log('[AnalyticsBoundaryProbe] entering trackReservationAnalyticsEvent call expression');
      trackReservationAnalyticsEvent({ unitId, eventType: 'page_view', locale, unitTimezone: bookingTimezone, writer: analyticsWriter });
      console.log('[AnalyticsBoundaryProbe] after trackReservationAnalyticsEvent call', { reachedAfterCall: true });
    } catch (error) {
      console.error('[AnalyticsBoundaryProbe] synchronous exception from trackReservationAnalyticsEvent call', sanitizeError(error));
      throw error;
    }
  }, [analyticsWriter, bookingTimezone, locale, settings, unitId]);

  useEffect(() => {
    const browserLang = navigator.language.split('-')[0];
    if (browserLang === 'en') {
      setLocale('en');
    }
  }, []);

  useEffect(() => {
    localeRef.current = locale;
  }, [locale]);

  useEffect(() => {
    let cancelled = false;
    setActiveTheme(null);
    if (!PUBLIC_THEME_STUDIO_RUNTIME_ENABLED || !unitId) return;
    loadThemeStudioActiveTheme(unitId).then(nextActiveTheme => {
      if (!cancelled) setActiveTheme(nextActiveTheme);
    });
    return () => {
      cancelled = true;
    };
  }, [unitId]);

  useEffect(() => {
    let cancelled = false;
    if (!unitId) {
      setLoadError('Hiányzik az egység azonosítója.');
      setLoading(false);
      return;
    }
    const fetchSettings = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const docSnap = await getPublicReservationSettings(unitId);
        if (cancelled) return;
        const defaultSettings: ReservationSetting = {
          id: unitId,
          blackoutDates: [],
          bookingClosures: [],
          dailyCapacity: null,
          capacityMode: 'daily',
          timeWindowCapacity: null,
          bucketMinutes: 15,
          bufferMinutes: 15,
          bookableWindow: { from: '11:00', to: '23:00' },
          kitchenStartTime: null,
          kitchenEndTime: null,
          barStartTime: null,
          barEndTime: null,
          serviceOpeningHours: [],
          guestForm: DEFAULT_GUEST_FORM,
          theme: defaultThemeSettings,
          uiTheme: 'minimal_glass',
          reservationMode: 'request',
          notificationEmails: [],
        };
        if (docSnap.exists()) {
          const dbData = docSnap.data() as any;
          const finalSettings: ReservationSetting = {
            ...defaultSettings,
            ...dbData,
            bookingClosures: normalizeBookingClosures(dbData.bookingClosures),
            guestForm: {
              ...DEFAULT_GUEST_FORM,
              ...(dbData.guestForm || {}),
            },
            theme: {
              ...defaultThemeSettings,
              ...(dbData.theme || {}),
            },
            capacityMode: dbData.capacityMode === 'timeWindow' ? 'timeWindow' : 'daily',
            bucketMinutes: Number.isFinite(dbData.bucketMinutes) ? dbData.bucketMinutes : 15,
            bufferMinutes: Number.isFinite(dbData.bufferMinutes) ? dbData.bufferMinutes : 15,
            serviceOpeningHours: normalizeServiceOpeningHours(dbData),
          };
          setSettings(finalSettings);
          const publicUnit = buildPublicUnit(unitId, finalSettings);
          setUnit(publicUnit);
          document.title = buildPublicReservationTitle(publicUnit.name);
        } else {
          setSettings(defaultSettings);
          const publicUnit = buildPublicUnit(unitId, defaultSettings);
          setUnit(publicUnit);
          document.title = buildPublicReservationTitle(publicUnit.name);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching reservation settings:', err);
        setLoadError(translations[localeRef.current].genericError);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void fetchSettings();
    return () => {
      cancelled = true;
    };
  }, [unitId]);

  useEffect(() => {
    if (!unitId || !settings) {
      setDailyHeadcounts(new Map());
      setCapacityByDate(new Map());
      return;
    }

    let cancelled = false;
    const fetchHeadcounts = async () => {
      setAvailabilityLoading(true);
      setAvailabilityLoadError(false);
      const startOfMonth = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        1
      );
      const endOfMonth = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() + 1,
        0,
        23,
        59,
        59
      );

      const startKey = toDateKey(startOfMonth);
      const endKey = toDateKey(endOfMonth);
      const q = query(
        collection(db, 'units', unitId, 'reservation_capacity'),
        where('date', '>=', startKey),
        where('date', '<=', endKey),
        orderBy('date')
      );

      try {
        const querySnapshot = await getDocs(q);
        const headcounts = new Map<string, number>();
        const capacityMap = new Map<string, ReservationCapacity>();
        querySnapshot.docs.forEach((docSnap) => {
          const capacity = docSnap.data() as ReservationCapacity;
          const dateKey = capacity.date || docSnap.id;
          const baseCount =
            typeof capacity.totalCount === 'number'
              ? capacity.totalCount
              : capacity.count || 0;
          const currentCount = headcounts.get(dateKey) || 0;
          headcounts.set(dateKey, currentCount + baseCount);
          capacityMap.set(dateKey, {
            date: dateKey,
            ...capacity,
            count: capacity.count ?? baseCount,
            totalCount: capacity.totalCount ?? baseCount,
          });
        });
        if (cancelled) return;
        setDailyHeadcounts(headcounts);
        setCapacityByDate(capacityMap);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching headcounts:', err);
        setAvailabilityLoadError(true);
      } finally {
        if (!cancelled) setAvailabilityLoading(false);
      }
    };

    void fetchHeadcounts();
    return () => {
      cancelled = true;
    };
  }, [unitId, currentMonth, settings]);

  useEffect(() => {
    syncThemeCssVariables(theme);
  }, [theme]);

  const resetFlow = () => {
    defaultHeadcountSyncRef.current = null;
    replayCredentialStorage.clear();
    setSelectedDate(null);
    setFormData({
      name: '',
      headcount: '2',
      startTime: '',
      endTime: '',
      phone: '',
      email: '',
      seatingPreference: 'any',
      customData: {},
    });
    setSubmittedData(null);
    setGraceAcceptance(false);
    setStep(1);
    setSubmitState('idle');
    setSubmitError(null);
    setAuthoritativeAvailability({ status: 'idle' });
  };

  useEffect(() => {
    defaultHeadcountSyncRef.current = null;
    replayCredentialStorage.clear();
    availabilityRequestSequenceRef.current += 1;
    setAuthoritativeAvailability({ status: 'idle' });
  }, [unitId, replayCredentialStorage]);

  const handleDateSelect = (day: Date) => {
    // Recheck with a fresh clock: a cutoff may pass after the calendar rendered.
    if (settings && reservationCalendarDayBlockReason(settings, toDateKey(day), Date.now())) return;
    trackReservationAnalyticsEvent({ unitId, eventType: 'reservation_started', locale, unitTimezone: bookingTimezone, writer: analyticsWriter });
    bookingFlowAnalytics.dateSelected({ requestedDateKey: toDateKey(day) });
    setSelectedDate(day);
    setGraceAcceptance(false);
    setStep(2);
  };

  const normalizePhone = (phone: string): string => {
    let cleaned = phone.replace(/[\s-()]/g, '');
    if (cleaned.startsWith('00')) cleaned = '+' + cleaned.substring(2);
    else if (cleaned.startsWith('06')) cleaned = '+36' + cleaned.substring(2);
    else if (!cleaned.startsWith('+')) cleaned = '+36' + cleaned;
    return cleaned;
  };

  const t = translations[locale];

  const emitValidationBlocked = (reasonCode: ValidationBlockReason) => {
    bookingFlowAnalytics.validationBlocked({ reasonCode });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitState === 'submitting' || !graceAcceptance) return;
    setSubmitError(null);
    setSubmitState('submitting');
    const traceId = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : uuidv4();
    let activeSubmitAttemptId: string | null = null;

    if (!selectedDate || !formData.startTime || !isValidReservationTime(formData.startTime) || !unit || !settings) {
      const normalized = normalizeSubmitError({
        error: new Error(t.errorRequired),
        traceId,
        isOnline: getOnlineStatus(),
        locale,
      });
      emitValidationBlocked(!selectedDate ? 'invalid_date' : 'missing_required_fields');
      setSubmitError(normalized);
      setSubmitState('error');
      return;
    }

    const headcount = Number(formData.headcount);
    if (!Number.isFinite(headcount) || headcount < 1) {
      const normalized = normalizeSubmitError({
        error: new Error(t.errorRequired),
        traceId,
        isOnline: getOnlineStatus(),
        locale,
      });
      emitValidationBlocked('invalid_headcount');
      setSubmitError(normalized);
      setSubmitState('error');
      return;
    }

    let startDateTime: Date;
    let endDateTime: Date;
    try {
      const { from: bookingStart, to: bookingEnd } = settings.bookableWindow || {
        from: '00:00',
        to: '23:59',
      };

      const selectedDateKey = toDateKey(selectedDate);
      if (formData.endTime && formData.endTime <= formData.startTime) {
        throw new Error(t.errorTime);
      }
      const interval = resolveBookingFlowAvailabilityInterval({
        requestedDateKey: selectedDateKey,
        startTimeBucket: formData.startTime,
        endTimeBucket: formData.endTime || null,
        timeZone: resolveUnitBookingTimezone(settings),
      });
      if (!interval) throw new Error(t.errorTime);
      startDateTime = new Date(interval.startTime);
      endDateTime = new Date(interval.endTime);
      if (Number.isNaN(startDateTime.getTime()) || Number.isNaN(endDateTime.getTime())) {
        throw new Error(t.errorTime);
      }
      if (formData.endTime && endDateTime.getTime() <= startDateTime.getTime()) {
        throw new Error(t.errorTime);
      }

      const [startHour, startMinute] = formData.startTime.split(':').map(Number);
      const defaultEndMinutes = (startHour * 60 + startMinute + BOOKING_FLOW_AVAILABILITY_DEFAULT_DURATION_MINUTES) % (24 * 60);
      const endTimeValue = formData.endTime || `${String(Math.floor(defaultEndMinutes / 60)).padStart(2, '0')}:${String(defaultEndMinutes % 60).padStart(2, '0')}`;
      if (formData.startTime < bookingStart || endTimeValue > bookingEnd) {
        const normalized = normalizeSubmitError({
          payload: { error: 'outside_bookable_window', bookingStart, bookingEnd },
          traceId,
          isOnline: getOnlineStatus(),
          locale,
        });
        emitValidationBlocked('invalid_time');
        setSubmitError(normalized);
        setSubmitState('error');
        return;
      }

      const groupReason = reservationGroupContactReason(settings, Number(formData.headcount));
      if (groupReason) {
        setSubmitError(normalizeSubmitError({ payload: { error: 'group_contact_required', reasonCode: groupReason }, traceId, isOnline: getOnlineStatus(), locale }));
        emitValidationBlocked('invalid_headcount'); setSubmitState('error'); return;
      }
      const timeLimitReason = reservationTimeLimitReason(settings, startDateTime, Date.now());
      if (timeLimitReason) {
        const normalized = normalizeSubmitError({ payload: { error: 'online_booking_closed', reasonCode: timeLimitReason }, traceId, isOnline: getOnlineStatus(), locale });
        emitValidationBlocked('invalid_time');
        setSubmitError(normalized);
        setSubmitState('error');
        return;
      }

      // The local capacity snapshot below is presentation-only. Final submit
      // authority remains the backend allocator used by the availability check.

      const reservationStatus: 'confirmed' | 'pending' =
        settings?.reservationMode === 'auto' ? 'confirmed' : 'pending';

      const baseReservation = {
        unitId,
        name: formData.name,
        headcount,
        startTime: Timestamp.fromDate(startDateTime),
        endTime: Timestamp.fromDate(endDateTime),
        contact: {
          phoneE164: normalizePhone(formData.phone),
          email: formData.email.trim().toLowerCase(),
        },
        locale,
        status: reservationStatus,
        reservationMode: settings.reservationMode,
        occasion: formData.customData['occasion'] || '',
        source: formData.customData['heardFrom'] || '',
        seatingPreference: formData.seatingPreference,
        customData: {
          ...formData.customData,
          seatingPreference: formData.seatingPreference,
        },
      };
      const dateKey = toDateKey(selectedDate);
      const submitAttempt = bookingFlowAnalytics.submitAttempted();
      activeSubmitAttemptId = submitAttempt.submitAttemptId;
      const bookingFlowAnalyticsCorrelation = submitAttempt.correlation
        ? { ...submitAttempt.correlation, replayCredential: replayCredentialStorage.getOrCreate(submitAttempt.correlation) }
        : undefined;

      let result;
      try {
        result = await guestCreateReservation({
          unitId,
          dateKey,
          bookingFlowAnalytics: bookingFlowAnalyticsCorrelation,
          reservation: {
            ...baseReservation,
            termsFingerprint: reservationTermsFingerprint(settings ?? {}),
            graceAcceptance: { schemaVersion: 1, accepted: true },
            startTime: startDateTime.toISOString(),
            endTime: endDateTime.toISOString(),
          },
        });
      } catch (error) {
        const transportFailureKind = resolveBookingFlowTransportFailureKind(error);
        if (activeSubmitAttemptId && transportFailureKind) {
          bookingFlowAnalytics.submitTransportFailed({ submitAttemptId: activeSubmitAttemptId, transportFailureKind });
        }
        throw error;
      }

      if (result.ok === false) {
        const normalized = normalizeSubmitError({
          response: new Response(null, { status: result.status }),
          payload: result.data,
          error: new Error(
            typeof result.data?.error === 'string'
              ? result.data.error
              : `guestCreateReservation failed (${result.status})`
          ),
          traceId,
          isOnline: getOnlineStatus(),
          locale,
        });
        if (result.data?.error === 'settings_changed') {
          normalized.userTitle = locale === 'hu' ? 'Megváltoztak a foglalási feltételek' : 'Reservation terms have changed';
          normalized.userMessage = locale === 'hu' ? 'Frissítsd az oldalt, és ellenőrizd az új feltételeket a foglalás előtt.' : 'Refresh the page and review the updated terms before booking.';
        }
        if (activeSubmitAttemptId && isControlledReservationRejection({ status: result.status, data: result.data })) {
          bookingFlowAnalytics.markSubmitRejected(activeSubmitAttemptId);
          replayCredentialStorage.clear();
        }
        setSubmitError(normalized);
        setSubmitState('error');
        return;
      }

      if (activeSubmitAttemptId) {
        bookingFlowAnalytics.markSubmitCreated(activeSubmitAttemptId);
        replayCredentialStorage.clear();
        activeSubmitAttemptId = null;
      }

      const payload = result.data;
      const referenceCode = payload.bookingId || payload.id;
      const manageToken = payload.manageToken as string | undefined;
      setSubmittedData({
        ...baseReservation,
        referenceCode,
        manageToken,
        startTime: Timestamp.fromDate(startDateTime),
        endTime: Timestamp.fromDate(endDateTime),
        date: selectedDate,
        capacityByDate,
      });
      trackReservationAnalyticsEvent({ unitId, eventType: 'reservation_completed', locale, unitTimezone: bookingTimezone, writer: analyticsWriter });
      setStep(3);
      setSubmitState('success');
    } catch (err: unknown) {
      console.error('Error during reservation submission:', err);
      const normalized = normalizeSubmitError({
        error: err,
        traceId,
        isOnline: getOnlineStatus(),
        locale,
      });
      setSubmitError(normalized);
      setSubmitState('error');
    }
  };

  const themeClassProps = useMemo(
    () => ({
      radiusClass: theme.radiusClass,
      shadowClass: theme.shadowClass,
      fontFamily: theme.fontFamilyClass,
      fontSize: theme.fontSizeClass,
      colors: theme.colors,
      infoPanelClass: theme.styles.infoPanel,
      uiTheme: theme.uiTheme,
    }),
    [theme]
  );

  const publicBusinessName = useMemo(
    () => resolvePublicBusinessName(settings as Record<string, any> | null),
    [settings]
  );
  const publicUnit = useMemo(
    () => unit ? { ...unit, name: publicBusinessName } : null,
    [publicBusinessName, unit]
  );

  const publicActiveTheme = PUBLIC_THEME_STUDIO_RUNTIME_ENABLED ? activeTheme : null;
  const headerBrandMode = publicActiveTheme?.activeAssetSnapshot?.brandHeader?.headerBrandMode?.mode === 'logo'
    ? 'logo'
    : publicActiveTheme?.activeAssetSnapshot?.brandHeader?.headerBrandMode?.mode === 'text'
      ? 'text'
      : settings?.theme?.headerBrandMode || 'text';
  const brandLogoUrl = useMemo(() => {
    if (headerBrandMode !== 'logo') return null;
    return resolveHeaderLogoUrl(settings, publicUnit, publicActiveTheme);
  }, [publicActiveTheme, headerBrandMode, publicUnit, settings]);

  const baseButtonClasses = useMemo(
    () => ({
      primaryButton: theme.styles.primaryButton,
      secondaryButton: theme.styles.secondaryButton,
      outlineButton: theme.styles.outlineButton,
    }),
    [theme.styles.outlineButton, theme.styles.primaryButton, theme.styles.secondaryButton]
  );

  const decorations = theme.uiTheme === 'playful_bubble' ? <PlayfulBubbles /> : undefined;
  const watermarkText = buildPublicReservationWatermark(publicBusinessName);
  const topRightLanguageSwitch = (
    <>
      <button
        type="button"
        aria-pressed={locale === 'hu'}
        aria-label="Magyar nyelv"
        onClick={() => setLocale('hu')}
        className={locale === 'hu' ? 'font-bold' : ''}
        style={{
          color: locale === 'hu' ? theme.colors.primary : theme.colors.textSecondary,
        }}
      >
        Magyar
      </button>
      <span style={{ color: theme.colors.textSecondary }}>|</span>
      <button
        type="button"
        aria-pressed={locale === 'en'}
        aria-label="English language"
        onClick={() => setLocale('en')}
        className={locale === 'en' ? 'font-bold' : ''}
        style={{
          color: locale === 'en' ? theme.colors.primary : theme.colors.textSecondary,
        }}
      >
        English
      </button>
    </>
  );

  const baseLayoutProps = {
    theme,
    isMinimalGlassTheme,
    decorations,
    watermarkText,
  };

  useEffect(() => {
    if (loadError && errorBannerRef.current) {
      errorBannerRef.current.focus();
    }
  }, [loadError, step]);

  useEffect(() => {
    const previousStep = previousStepRef.current;
    previousStepRef.current = step;
    if (loading || loadError || submitError || previousStep === step) return;
    stepRegionRef.current?.focus();
  }, [loadError, loading, step, submitError]);

  const timeWindowSelection = useMemo(() => {
    if (!settings || (settings.capacityMode ?? 'daily') !== 'timeWindow') {
      return {
        dateKey: null,
        startTime: null,
        endTime: null,
        bucketKeys: [] as string[],
        remaining: null as number | null,
        overLimit: false,
      };
    }
    if (!selectedDate || !formData.startTime) {
      return {
        dateKey: null,
        startTime: null,
        endTime: null,
        bucketKeys: [] as string[],
        remaining: null as number | null,
        overLimit: false,
      };
    }
    if (formData.endTime && formData.endTime <= formData.startTime) {
      return {
        dateKey: null,
        startTime: null,
        endTime: null,
        bucketKeys: [] as string[],
        remaining: null as number | null,
        overLimit: false,
      };
    }
    const interval = resolveBookingFlowAvailabilityInterval({
      requestedDateKey: toDateKey(selectedDate),
      startTimeBucket: formData.startTime,
      endTimeBucket: formData.endTime || null,
      timeZone: resolveUnitBookingTimezone(settings),
    });
    const startDateTime = interval ? new Date(interval.startTime) : new Date(Number.NaN);
    const endDateTime = interval ? new Date(interval.endTime) : new Date(Number.NaN);
    if (Number.isNaN(startDateTime.getTime()) || Number.isNaN(endDateTime.getTime())) {
      return {
        dateKey: null,
        startTime: null,
        endTime: null,
        bucketKeys: [] as string[],
        remaining: null as number | null,
        overLimit: false,
      };
    }
    if (endDateTime.getTime() <= startDateTime.getTime()) {
      return {
        dateKey: null,
        startTime: startDateTime,
        endTime: endDateTime,
        bucketKeys: [] as string[],
        remaining: null as number | null,
        overLimit: false,
      };
    }
    const bucketMinutes = settings.bucketMinutes ?? 15;
    const bufferMinutes = settings.bufferMinutes ?? 15;
    const bucketKeys = computeReservationBucketKeysClient({
      startTime: startDateTime,
      endTime: endDateTime,
      bufferMinutes,
      bucketMinutes,
    });
    const dateKey = toDateKey(selectedDate);
    const capacityEntry = capacityByDate.get(dateKey);
    const byTimeBucket = capacityEntry?.byTimeBucket ?? {};
    const remaining =
      timeWindowCapacityValue && bucketKeys.length
        ? bucketKeys.reduce(
            (min, key) => Math.min(min, timeWindowCapacityValue - (byTimeBucket[key] ?? 0)),
            timeWindowCapacityValue
          )
        : null;
    const headcount = Number(formData.headcount);
    const overLimit =
      timeWindowCapacityValue && bucketKeys.length && Number.isFinite(headcount)
        ? bucketKeys.some(
            key => (byTimeBucket[key] ?? 0) + headcount > timeWindowCapacityValue
          )
        : false;
    return {
      dateKey,
      startTime: startDateTime,
      endTime: endDateTime,
      bucketKeys,
      remaining: remaining !== null ? Math.max(0, remaining) : null,
      overLimit,
    };
  }, [
    capacityByDate,
    formData.endTime,
    formData.headcount,
    formData.startTime,
    selectedDate,
    settings,
    timeWindowCapacityValue,
  ]);

  useEffect(() => {
    if (step !== 2 || !selectedDate) return;
    const headcount = Number(formData.headcount);
    if (!Number.isFinite(headcount) || headcount < 1) return;
    const syncKey = `${toDateKey(selectedDate)}:${headcount}`;
    if (defaultHeadcountSyncRef.current === syncKey) return;
    defaultHeadcountSyncRef.current = syncKey;
    bookingFlowAnalytics.headcountSelected({ headcount });
  }, [bookingFlowAnalytics, formData.headcount, selectedDate, step]);

  useEffect(() => {
    const sequence = ++availabilityRequestSequenceRef.current;
    if (
      step !== 2
      || !selectedDate
      || !settings
      || !isValidReservationTime(formData.startTime)
      || Boolean(formData.endTime && formData.endTime <= formData.startTime)
    ) {
      setAuthoritativeAvailability({ status: 'idle' });
      return;
    }

    const headcount = Number(formData.headcount);
    if (
      !Number.isSafeInteger(headcount)
      || headcount < 1
      || headcount > BOOKING_FLOW_AVAILABILITY_MAX_HEADCOUNT
    ) {
      setAuthoritativeAvailability({ status: 'idle' });
      return;
    }

    const requestedDateKey = toDateKey(selectedDate);
    const interval = resolveBookingFlowAvailabilityInterval({
      requestedDateKey,
      startTimeBucket: formData.startTime,
      endTimeBucket: formData.endTime || null,
      timeZone: resolveUnitBookingTimezone(settings),
    });
    if (!interval) {
      setAuthoritativeAvailability({ status: 'idle' });
      return;
    }

    const durationMinutes = (Date.parse(interval.endTime) - Date.parse(interval.startTime)) / 60_000;
    if (!Number.isSafeInteger(durationMinutes) || durationMinutes < 1) {
      setAuthoritativeAvailability({ status: 'idle' });
      return;
    }
    const identityState = bookingFlowAnalytics.synchronizeAvailabilitySelection({
      requestedDateKey,
      startTimeBucket: formData.startTime,
      headcount,
      durationMinutes,
    });
    if (
      !identityState
      || identityState.canonicalSelection.requestedDateKey !== requestedDateKey
      || identityState.canonicalSelection.startTimeBucket !== formData.startTime
      || identityState.canonicalSelection.headcount !== headcount
      || identityState.canonicalSelection.durationMinutes !== durationMinutes
    ) {
      setAuthoritativeAvailability({ status: 'idle' });
      return;
    }

    const request: BookingFlowAvailabilityRequest = {
      unitId,
      bookingFlowId: identityState.bookingFlowId,
      selectionRevision: identityState.selectionRevision,
      requestedDateKey,
      startTime: interval.startTime,
      endTime: interval.endTime,
      startTimeBucket: formData.startTime,
      headcount,
    };
    const selectionKey = [
      requestedDateKey,
      formData.startTime,
      formData.endTime || '',
      String(headcount),
    ].join('|');
    const controller = new AbortController();
    setAuthoritativeAvailability({ status: 'loading' });

    const requestTimeout = window.setTimeout(() => {
      void requestBookingFlowAvailability(request, { signal: controller.signal }).then(response => {
        if (controller.signal.aborted || availabilityRequestSequenceRef.current !== sequence) return;
        const current = bookingFlowAnalytics.getCurrentState();
        if (
          !current
          || current.bookingFlowId !== request.bookingFlowId
          || current.selectionRevision !== request.selectionRevision
          || current.canonicalSelection.requestedDateKey !== request.requestedDateKey
          || current.canonicalSelection.startTimeBucket !== request.startTimeBucket
          || current.canonicalSelection.headcount !== request.headcount
          || current.canonicalSelection.durationMinutes !== durationMinutes
        ) return;
        setAuthoritativeAvailability({
          status: 'ready',
          response,
          durationMinutes,
          requestSequence: sequence,
          selectionKey,
        });
      }).catch(() => {
        if (controller.signal.aborted || availabilityRequestSequenceRef.current !== sequence) return;
        setAuthoritativeAvailability({ status: 'error' });
      });
    }, 180);

    return () => {
      window.clearTimeout(requestTimeout);
      controller.abort();
    };
  }, [
    bookingFlowAnalytics,
    formData.endTime,
    formData.headcount,
    formData.startTime,
    selectedDate,
    settings,
    step,
    unitId,
  ]);

  useEffect(() => {
    if (authoritativeAvailability.status !== 'ready') return;
    const { response, durationMinutes, requestSequence } = authoritativeAvailability;
    const current = bookingFlowAnalytics.getCurrentState();
    if (
      step !== 2
      || availabilityRequestSequenceRef.current !== requestSequence
      || !current
      || current.bookingFlowId !== response.bookingFlowId
      || current.selectionRevision !== response.selectionRevision
      || current.canonicalSelection.requestedDateKey !== response.requestedDateKey
      || current.canonicalSelection.startTimeBucket !== response.startTimeBucket
      || current.canonicalSelection.headcount !== response.headcount
      || current.canonicalSelection.durationMinutes !== durationMinutes
    ) return;
    bookingFlowAnalytics.availabilityResultPresented({
      availabilityDecisionId: response.availabilityDecisionId,
      selectionRevision: response.selectionRevision,
      requestedDateKey: response.requestedDateKey,
      startTimeBucket: response.startTimeBucket,
      headcount: response.headcount,
      durationMinutes,
      availabilityResult: response.availabilityResult,
    });
  }, [authoritativeAvailability, bookingFlowAnalytics, step]);

  const recordRenderReturn = (branch: string) => {
    recordRuntimeDiagnosticEvent({ event: 'render:return', componentInstanceId: diagnosticInstanceId, unitId, branch });
  };

  const retryLabel = locale === 'hu' ? 'Próbálja újra' : 'Try again';

  if (loadError && step !== 2) {
    recordRenderReturn('load_error');
    return (
      <PublicReservationLayout
        {...baseLayoutProps}
        header={
          <h2
            className={`text-xl font-bold ${
              isMinimalGlassTheme ? 'text-[var(--color-text-primary)]' : ''
            }`}
            style={{ color: 'var(--color-danger)' }}
          >
            Hiba
          </h2>
        }
        body={
          <div ref={errorBannerRef} tabIndex={-1} aria-live="assertive">
            <p
              className={`mt-2 ${
                isMinimalGlassTheme ? 'text-[var(--color-text-secondary)]' : ''
              }`}
            >
              {loadError}
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className={`mt-4 ${baseButtonClasses.primaryButton} ${themeClassProps.radiusClass}`}
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              {retryLabel}
            </button>
          </div>
        }
      />
    );
  }

  if (loading || !publicUnit || !settings) {
    recordRenderReturn('loading');
    return (
      <PublicReservationLayout
        {...baseLayoutProps}
        header={
          <div role="status" aria-live="polite" className="flex flex-col items-center gap-3 text-center">
            <LoadingSpinner />
            <p className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
              {locale === 'hu' ? 'Foglalási oldal betöltése…' : 'Loading reservation page…'}
            </p>
          </div>
        }
        body={
          <div className="grid gap-3" aria-hidden="true">
            <div className="h-24 animate-pulse rounded-2xl bg-white/30 motion-reduce:animate-none" />
            <div className="h-40 animate-pulse rounded-2xl bg-white/20 motion-reduce:animate-none" />
          </div>
        }
      />
    );
  }

  const headerSection = (
    <>
      {brandLogoUrl ? (
        <img
          src={brandLogoUrl}
          alt={publicBusinessName}
          className="max-h-16 md:max-h-20 max-w-[70%] object-contain"
        />
      ) : (
        <h1
          className={`text-4xl font-bold ${
            isMinimalGlassTheme ? 'text-[var(--color-text-primary)]' : ''
          }`}
          style={{ color: 'var(--color-text-primary)' }}
        >
          {publicBusinessName}
        </h1>
      )}
      <p
        className={`text-lg mt-1 ${
          isMinimalGlassTheme ? 'text-[var(--color-text-secondary)]' : ''
        }`}
        style={{ color: 'var(--color-text-secondary)' }}
      >
        {t.title}
      </p>
      <ReservationVenueMap location={settings?.venueLocation} embedApiKey={settings?.mapsEmbedApiKey} locale={locale === 'en' ? 'en' : 'hu'} />
      <div className="w-full flex-shrink-0">
        <ProgressIndicator currentStep={step} t={t} theme={theme} locale={locale} />
      </div>
    </>
  );

  const currentStepLabel = step === 1 ? t.step1 : step === 2 ? t.step2 : t.step3;
  const stepPaneBase = 'w-full flex-shrink-0 flex-col min-h-0 transition-opacity duration-300 motion-reduce:transition-none';
  const isStepOne = step === 1;


  const bodySection = (
    <div
      ref={stepRegionRef}
      tabIndex={-1}
      role="region"
      aria-labelledby="reservation-current-step-heading"
      className={`flex flex-col gap-3 focus:outline-none md:gap-4 ${isStepOne ? 'min-h-0' : 'min-h-full'}`}
    >
      <h2 id="reservation-current-step-heading" className="sr-only">{currentStepLabel}</h2>
      {selectedDate && step === 2 && normalizeBookingClosures(settings.bookingClosures).some(closure => closure.date === toDateKey(selectedDate)) && (
        <aside role="status" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-950">
          <p className="font-semibold">{locale === 'hu' ? 'Időszakos foglalási korlátozás' : 'Temporary reservation restriction'}</p>
          <p className="text-sm">{locale === 'hu' ? 'A kiválasztott napon az alábbi időszakban nem fogadunk foglalásokat.' : 'We are unable to accept reservations during the following hours on the selected date.'}</p>
          <ul className="text-sm">{normalizeBookingClosures(settings.bookingClosures).filter(closure => closure.date === toDateKey(selectedDate)).map(closure => <li key={closure.id}>{closure.from}–{closure.to}</li>)}</ul>
        </aside>
      )}
      {(
        <VenueTrustCard
          unit={publicUnit}
          settings={settings}
          brandLogoUrl={brandLogoUrl}
          themeProps={themeClassProps}
          t={t}
          locale={locale}
        />
      )}
      <div className={isStepOne ? 'min-h-0 overflow-x-hidden overflow-y-visible' : 'flex-1 min-h-0 overflow-x-hidden overflow-y-visible md:overflow-y-auto'}>
        <div
          className="flex w-full transition-transform duration-500 ease-in-out motion-reduce:transition-none md:[transform:translateX(var(--reservation-step-offset))]"
          style={{ '--reservation-step-offset': `-${(step - 1) * 100}%` } as React.CSSProperties}
        >
          <div
            aria-hidden={step !== 1}
            className={`${stepPaneBase} ${step === 1 ? 'flex' : 'hidden md:flex'} ${isStepOne ? 'h-auto' : 'h-full'} ${
              step === 1 ? 'opacity-100' : 'opacity-50 md:opacity-70'
            }`}
          >
            <div className="flex-1 min-h-0">
              <Step1Date
                settings={settings}
                onDateSelect={handleDateSelect}
                themeProps={themeClassProps}
                t={t}
                locale={locale}
                currentMonth={currentMonth}
                onMonthChange={setCurrentMonth}
                dailyHeadcounts={dailyHeadcounts}
                capacityByDate={capacityByDate}
                availabilityLoading={availabilityLoading}
                availabilityLoadError={availabilityLoadError}
              />
            </div>
          </div>
          <div
            aria-hidden={step !== 2}
            className={`${stepPaneBase} ${step === 2 ? 'flex' : 'hidden md:flex'} ${
              step === 2 ? 'opacity-100' : 'opacity-50 md:opacity-70'
            }`}
          >
            <div className="flex-1 min-h-0">
              {selectedDate ? (
                <Step2Details
                  selectedDate={selectedDate}
                  formData={formData}
                  setFormData={setFormData}
                  onBack={() => {
                    setStep(1);
                    setSubmitError(null);
                    setSubmitState('idle');
                  }}
                  onSubmit={handleSubmit}
                  graceAcknowledged={graceAcceptance}
                  setGraceAcknowledged={setGraceAcceptance}
                  isSubmitting={isSubmitting}
                  settings={settings}
                  timeWindowRemaining={timeWindowSelection.remaining}
                  timeWindowCapacity={timeWindowCapacityValue}
                  timeWindowOverLimit={timeWindowSelection.overLimit}
                  authoritativeAvailability={authoritativeAvailability}
                  themeProps={themeClassProps}
                  t={t}
                  locale={locale}
                  error={submitError}
                  onRetry={() => {
                    setSubmitError(null);
                    setSubmitState('idle');
                  }}
                  buttonClasses={{
                    primary: `${baseButtonClasses.primaryButton} ${themeClassProps.radiusClass}`,
                    secondary: `${baseButtonClasses.secondaryButton} ${themeClassProps.radiusClass}`,
                  }}
                  unit={publicUnit}
                  onHeadcountSelected={headcount => bookingFlowAnalytics.headcountSelected({ headcount })}
                  onTimeSelected={(time, source) => bookingFlowAnalytics.timeSelected({ startTimeBucket: time, source })}
                  onContactFormStarted={() => bookingFlowAnalytics.contactFormStarted()}
                  onValidationBlocked={emitValidationBlocked}
                />
              ) : null}
            </div>
          </div>
          <div
            aria-hidden={step !== 3}
            className={`${stepPaneBase} ${step === 3 ? 'flex' : 'hidden md:flex'} ${
              step === 3 ? 'opacity-100' : 'opacity-50 md:opacity-70'
            }`}
          >
            <div className="flex-1 min-h-0">
              <Step3Confirmation
                onReset={resetFlow}
                theme={theme}
                themeProps={themeClassProps}
                t={t}
                submittedData={submittedData}
                unit={publicUnit}
                locale={locale}
                settings={settings}
                buttonClasses={{
                  primary: `${baseButtonClasses.primaryButton} ${themeClassProps.radiusClass}`,
                  secondary: `${baseButtonClasses.secondaryButton} ${themeClassProps.radiusClass}`,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );



  recordRenderReturn(step === 1 ? 'step_date' : step === 2 ? 'step_details' : 'success');
  return (
    <PublicReservationLayout
      {...baseLayoutProps}
      header={headerSection}
      body={bodySection}
      compactBody={step === 1}
      topRightContent={topRightLanguageSwitch}
    />
  );
};

const Step1Date: React.FC<{
  settings: ReservationSetting;
  onDateSelect: (date: Date) => void;
  themeProps: any;
  t: any;
  locale: Locale;
  currentMonth: Date;
  onMonthChange: (date: Date) => void;
  dailyHeadcounts: Map<string, number>;
  capacityByDate: Map<string, ReservationCapacity>;
  availabilityLoading: boolean;
  availabilityLoadError: boolean;
}> = ({
  settings,
  onDateSelect,
  themeProps,
  t,
  locale,
  currentMonth,
  onMonthChange,
  dailyHeadcounts,
  capacityByDate,
  availabilityLoading,
  availabilityLoadError,
}) => {
  const startOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  );
  const endOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  );
  const days: (Date | null)[] = [];
  const startDayOfWeek = (startOfMonth.getDay() + 6) % 7;

  for (let i = 0; i < startDayOfWeek; i++) {
    days.push(null);
  }
  for (let i = 1; i <= endOfMonth.getDate(); i++) {
    days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i));
  }

  const blackoutSet = new Set(settings.blackoutDates || []);
  const [calendarNowMs, setCalendarNowMs] = useState(() => Date.now());
  useEffect(() => {
    // Keep current-day availability fresh while the guest is on the page.
    const timer = window.setInterval(() => setCalendarNowMs(Date.now()), 10_000);
    return () => window.clearInterval(timer);
  }, []);

  const formatAccessibleDate = (date: Date) =>
    date.toLocaleDateString(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const calendarStatusCopy = {
    available: t.calendarAvailable || (locale === 'hu' ? 'Elérhető' : 'Available'),
    unavailable: t.calendarUnavailable || (locale === 'hu' ? 'Nem elérhető' : 'Unavailable'),
    closed: t.calendarClosed || (locale === 'hu' ? 'Zárva' : 'Closed'),
    full: t.calendarFull || t.errorCapacityFull,
  };

  return (
    <div
      className={`p-4 pb-6 md:p-6 ${themeProps.radiusClass} ${themeProps.shadowClass}`}
      style={{
        backgroundColor: themeProps.colors.surface,
        color: themeProps.colors.textPrimary,
        border: `1px solid ${themeProps.colors.surface}`,
      }}
    >
      <h2 className="mb-2 text-center text-xl font-semibold text-[var(--color-text-primary)] md:mb-3 md:text-2xl">
        {t.step1Title}
      </h2>
      <div className="mb-2 flex items-center justify-between md:mb-4">
        <button
          type="button"
          aria-label={locale === 'hu' ? 'Előző hónap' : 'Previous month'}
          onClick={() =>
            onMonthChange(
              new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
            )
          }
          className={`min-h-11 min-w-11 p-2 ${themeProps.radiusClass} transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]`}
          style={{
            backgroundColor: themeProps.colors.surface,
            color: themeProps.colors.textPrimary,
          }}
        >
          <span aria-hidden="true">&lt;</span>
        </button>
        <div className="text-center">
          <h3 className="font-bold text-lg" aria-live="polite">
            {t.monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h3>
          <p className={`text-xs ${availabilityLoading || availabilityLoadError ? '' : 'hidden md:block'}`} style={{ color: themeProps.colors.textSecondary }}>
            {availabilityLoading
              ? locale === 'hu' ? 'Elérhetőség frissítése…' : 'Refreshing availability…'
              : availabilityLoadError
              ? locale === 'hu' ? 'Az elérhetőség frissítése nem sikerült.' : 'Availability could not be refreshed.'
              : locale === 'hu' ? 'Válasszon egy elérhető napot.' : 'Choose an available day.'}
          </p>
        </div>
        <button
          type="button"
          aria-label={locale === 'hu' ? 'Következő hónap' : 'Next month'}
          onClick={() =>
            onMonthChange(
              new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
            )
          }
          className={`min-h-11 min-w-11 p-2 ${themeProps.radiusClass} transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]`}
          style={{
            backgroundColor: themeProps.colors.surface,
            color: themeProps.colors.textPrimary,
          }}
        >
          <span aria-hidden="true">&gt;</span>
        </button>
      </div>
      {availabilityLoading && (
        <div role="status" aria-live="polite" className="sr-only">
          {locale === 'hu' ? 'Elérhetőség frissítése folyamatban.' : 'Availability refresh in progress.'}
        </div>
      )}
      <div className="grid grid-cols-7 gap-1 text-center font-semibold text-[var(--color-text-secondary)] text-sm mb-2">
        {t.dayNames.map((d: string) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1" role="group" aria-label={locale === 'hu' ? 'Választható foglalási napok' : 'Selectable reservation dates'}>
        {days.map((day, i) => {
          if (!day) return <div key={`empty-${i}`} aria-hidden="true"></div>;
          const dateKey = toDateKey(day);

          const isBlackout = blackoutSet.has(dateKey);
          const dayBlockReason = reservationCalendarDayBlockReason(settings, dateKey, calendarNowMs);
          const isPast = dayBlockReason === 'PAST_DAY';
          const isPolicyBlocked = dayBlockReason !== null && !isPast;
          let isFull = false;
          if ((settings.capacityMode ?? 'daily') === 'daily') {
            const currentHeadcount = dailyHeadcounts.get(dateKey) || 0;
            const dateSpecificLimit = capacityByDate.get(dateKey)?.limit;
            const effectiveLimit =
              typeof dateSpecificLimit === 'number'
                ? dateSpecificLimit
                : settings.dailyCapacity;
            if (typeof effectiveLimit === 'number' && effectiveLimit >= 0) {
              isFull = currentHeadcount >= effectiveLimit;
            }
          }
          const isDisabled = isBlackout || isPast || isPolicyBlocked || isFull;
          const statusLabel = isFull
            ? calendarStatusCopy.full
            : isBlackout
            ? calendarStatusCopy.closed
            : calendarStatusCopy.available;

          let buttonClass = `w-full p-1 min-h-12 flex flex-col items-center justify-center text-sm border ${themeProps.radiusClass} transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]`;
          const titleText = isPast || isPolicyBlocked
            ? formatAccessibleDate(day)
            : `${formatAccessibleDate(day)} — ${statusLabel}`;

          const buttonStyle = isFull
            ? {
                backgroundColor: toRgba(themeProps.colors.danger, 0.12),
                borderColor: toRgba(themeProps.colors.danger, 0.35),
                color: themeProps.colors.danger,
              }
            : isPast || isPolicyBlocked || isBlackout
            ? {
                backgroundColor: toRgba(themeProps.colors.surface, isPast ? 0.55 : 0.82),
                borderColor: toRgba(themeProps.colors.textSecondary, 0.18),
                color: themeProps.colors.textSecondary,
              }
            : {
                backgroundColor: toRgba(themeProps.colors.success || themeProps.colors.primary, 0.12),
                borderColor: toRgba(themeProps.colors.success || themeProps.colors.primary, 0.35),
                color: themeProps.colors.textPrimary,
              };

          if (isDisabled) {
            buttonClass += isFull
              ? ' line-through cursor-not-allowed'
              : ' cursor-not-allowed opacity-75';
          } else {
            buttonClass += ' hover:opacity-90';
          }

          return (
            <div key={dateKey}>
              <button
                type="button"
                onClick={() => onDateSelect(day)}
                disabled={isDisabled}
                title={titleText}
                aria-label={titleText}
                className={buttonClass}
                style={buttonStyle}
              >
                <span>{day.getDate()}</span>
                {isDisabled && !isPast && !isPolicyBlocked && (
                  <span className="mt-0.5 hidden max-w-full truncate text-[10px] leading-none no-underline md:inline">
                    {statusLabel}
                  </span>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};


type StandardFieldKey = 'name' | 'phone' | 'email' | 'headcount' | 'startTime' | 'endTime';
type CustomFieldErrors = Record<string, string>;
type CustomFieldTouched = Record<string, boolean>;
type FormErrors = Record<StandardFieldKey, string> & { customData: CustomFieldErrors };
type TouchedFields = Record<StandardFieldKey, boolean> & { customData: CustomFieldTouched };
type PublicCustomSelectField = CustomSelectField & { required?: boolean; enabled?: boolean; hidden?: boolean };

const STANDARD_FIELD_KEYS: StandardFieldKey[] = ['name', 'phone', 'email', 'headcount', 'startTime', 'endTime'];

const isCustomSelectEnabled = (field: CustomSelectField): boolean => {
  const fieldConfig = field as PublicCustomSelectField;
  return fieldConfig.enabled !== false && fieldConfig.hidden !== true;
};

const isCustomSelectRequired = (field: CustomSelectField): boolean => {
  const fieldConfig = field as PublicCustomSelectField;
  return isCustomSelectEnabled(field) && fieldConfig.required === true;
};

const isReservationDetailsFormComplete = (
  formData: {
    name?: string;
    phone?: string;
    email?: string;
    headcount?: string;
    startTime?: string;
    customData?: Record<string, string>;
  },
  settings: ReservationSetting,
): boolean => {
  const requiredCustomSelectsComplete = (settings.guestForm?.customSelects || [])
    .filter(isCustomSelectRequired)
    .every((field: CustomSelectField) => Boolean(formData.customData[field.id]));

  return (
    isReservationFormSubmittable(formData) &&
    Boolean(formData.name?.trim()) &&
    /^\+?[0-9\s-()]{7,}$/.test(formData.phone || '') &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email || '') &&
    Number.isFinite(Number(String(formData.headcount ?? ''))) &&
    Number(String(formData.headcount ?? '')) >= 1 &&
    isValidReservationTime(formData.startTime || '') &&
    requiredCustomSelectsComplete
  );
};

interface Step2DetailsProps {
  selectedDate: Date | null;
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  onBack: () => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isSubmitting: boolean;
  settings: ReservationSetting;
  timeWindowRemaining: number | null;
  timeWindowCapacity: number | null;
  timeWindowOverLimit: boolean;
  authoritativeAvailability: AuthoritativeAvailabilityState;
  themeProps: any;
  t: any;
  locale: Locale;
  error: SubmitError | null;
  onRetry: () => void;
  buttonClasses: { primary: string; secondary: string };
  unit: Unit;
  onHeadcountSelected: (headcount: number) => void;
  onTimeSelected: (time: string, source: TimeSelectionSource) => void;
  onContactFormStarted: () => void;
  onValidationBlocked: (reasonCode: ValidationBlockReason) => void;
  graceAcknowledged: boolean;
  setGraceAcknowledged: (value: boolean) => void;
}

const Step2Details: React.FC<Step2DetailsProps> = ({
  selectedDate,
  formData,
  setFormData,
  onBack,
  onSubmit,
  isSubmitting,
  settings,
  timeWindowRemaining,
  timeWindowCapacity,
  timeWindowOverLimit,
  authoritativeAvailability,
  themeProps,
  t,
  locale,
  error,
  onRetry,
  buttonClasses,
  unit,
  onHeadcountSelected,
  onTimeSelected,
  onContactFormStarted,
  onValidationBlocked,
  graceAcknowledged,
  setGraceAcknowledged,
}) => {
  const [graceAcceptanceError, setGraceAcceptanceError] = useState(false);
  const graceCheckboxRef = useRef<HTMLInputElement | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({
    name: '',
    phone: '',
    email: '',
    headcount: '',
    startTime: '',
    endTime: '',
    customData: {} as Record<string, string>,
  });
  const [touchedFields, setTouchedFields] = useState<TouchedFields>({
    name: false,
    phone: false,
    email: false,
    headcount: false,
    startTime: false,
    endTime: false,
    customData: {} as Record<string, boolean>,
  });
  const [timeLimitNowMs, setTimeLimitNowMs] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setTimeLimitNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const selectedTimeInterval = formData.startTime ? resolveBookingFlowAvailabilityInterval({ requestedDateKey: toDateKey(selectedDate), startTimeBucket: formData.startTime, endTimeBucket: formData.endTime || null, timeZone: resolveUnitBookingTimezone(settings) }) : null;
  const selectedTimeLimitReason = selectedTimeInterval ? reservationTimeLimitReason(settings, new Date(selectedTimeInterval.startTime), timeLimitNowMs) : null;
  const selectedGroupReason = reservationGroupContactReason(settings, Number(formData.headcount));
  const groupSummary = reservationGroupSummary(settings, locale);
  const graceMinutes = reservationGraceMinutes(settings, Number(formData.headcount));
  const errorBannerRef = useRef<HTMLDivElement | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const phoneInputRef = useRef<HTMLInputElement | null>(null);
  const headcountInputRef = useRef<HTMLInputElement | null>(null);
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const startTimeInputRef = useRef<HTMLInputElement | null>(null);
  const endTimeInputRef = useRef<HTMLInputElement | null>(null);
  const customSelectRefs = useRef<Record<string, HTMLSelectElement | null>>({});

  const getFieldErrorId = (name: string) => `reservation-${name}-error`;
  const getCustomFieldErrorId = (id: string) => `reservation-custom-${id}-error`;

  const validateField = (name: StandardFieldKey, value: string) => {
    if (!value.trim()) {
      if (name === 'name') return t.errorNameRequired;
      if (name === 'phone') return t.errorPhoneRequired;
      if (name === 'email') return t.errorEmailRequired;
      if (name === 'headcount') return t.errorHeadcountRequired;
      if (name === 'startTime') return t.errorStartTimeRequired;
      return t.errorRequired;
    }
    if ((name === 'startTime' || name === 'endTime') && !isValidReservationTime(value)) return t.errorTime;
    if (name === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
      return t.errorInvalidEmail;
    if (name === 'headcount' && (!Number.isSafeInteger(Number(value)) || Number(value) < 1 || Number(value) > BOOKING_FLOW_AVAILABILITY_MAX_HEADCOUNT)) return locale === 'hu' ? 'Pozitív, egész létszámot adjon meg.' : 'Enter a positive whole party size.';
    if (name === 'phone' && !/^\+?[0-9\s-()]{7,}$/.test(value))
      return t.errorInvalidPhone;
    return '';
  };

  const handleStandardChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
    if (['name', 'phone', 'email'].includes(name) && value.trim()) onContactFormStarted();
    if (name === 'headcount') {
      const headcount = Number(value);
      if (Number.isFinite(headcount) && headcount >= 1) onHeadcountSelected(headcount);
    }
    if (name === 'startTime' && isValidReservationTime(value)) onTimeSelected(value, 'manual');
    if (STANDARD_FIELD_KEYS.includes(name as StandardFieldKey)) {
      const fieldName = name as StandardFieldKey;
      if (!touchedFields[fieldName]) return;
      setFormErrors(prev => ({
        ...prev,
        [fieldName]: fieldName === 'endTime' && !value ? '' : validateField(fieldName, value),
      }));
    }
  };

  const handleStandardBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (!STANDARD_FIELD_KEYS.includes(name as StandardFieldKey)) return;
    const fieldName = name as StandardFieldKey;
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
    setFormErrors(prev => ({
      ...prev,
      [fieldName]: fieldName === 'endTime' && !value ? '' : validateField(fieldName, value),
    }));
  };

  const validateCustomField = (field: CustomSelectField, value: string) => {
    if (!isCustomSelectRequired(field)) return '';
    if (value) return '';
    const label = resolvePublicLabel(field, locale);
    return t.errorCustomSelectRequired.replace('{label}', label);
  };

  const buildCustomErrors = () => {
    const customErrors: Record<string, string> = {};
    settings.guestForm?.customSelects
      ?.filter(isCustomSelectEnabled)
      .forEach((field: CustomSelectField) => {
      customErrors[field.id] = validateCustomField(field, formData.customData[field.id] || '');
    });
    return customErrors;
  };

  const focusFirstInvalidField = (errors: typeof formErrors) => {
    const refs: Record<StandardFieldKey, React.RefObject<HTMLInputElement | null>> = {
      name: nameInputRef,
      phone: phoneInputRef,
      headcount: headcountInputRef,
      email: emailInputRef,
      startTime: startTimeInputRef,
      endTime: endTimeInputRef,
    };
    const firstInvalid = STANDARD_FIELD_KEYS.find(key => errors[key]);
    if (firstInvalid) {
      refs[firstInvalid].current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      refs[firstInvalid].current?.focus();
      return;
    }
    const firstInvalidCustom = Object.keys(errors.customData || {}).find(key => errors.customData[key]);
    if (firstInvalidCustom) {
      const customRef = customSelectRefs.current[firstInvalidCustom];
      customRef?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      customRef?.focus();
    }
  };

  const validateRequiredFields = () => {
    const nextErrors = {
      name: validateField('name', formData.name || ''),
      phone: validateField('phone', formData.phone || ''),
      headcount: validateField('headcount', String(formData.headcount ?? '')),
      email: validateField('email', formData.email || ''),
      startTime: validateField('startTime', formData.startTime || ''),
      endTime: formData.endTime
        ? validateField('endTime', formData.endTime) || (formData.startTime && formData.endTime <= formData.startTime ? t.errorTime : '')
        : '',
      customData: buildCustomErrors(),
    };
    return nextErrors;
  };

  const handleValidatedSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    const nextErrors = validateRequiredFields();
    if (selectedTimeInterval) {
      const reason = reservationTimeLimitReason(settings, new Date(selectedTimeInterval.startTime), Date.now());
      if (reason) nextErrors.startTime = reservationTimeLimitMessage(reason, locale);
    }
    const hasErrors =
      Object.entries(nextErrors).some(([key, value]) => key !== 'customData' && Boolean(value)) ||
      Object.values(nextErrors.customData).some(Boolean);
    if (hasErrors) {
      event.preventDefault();
      const reasonCode: ValidationBlockReason = nextErrors.headcount
        ? 'invalid_headcount'
        : nextErrors.startTime || nextErrors.endTime
        ? 'invalid_time'
        : 'missing_required_fields';
      onValidationBlocked(reasonCode);
      setFormErrors(nextErrors);
      setTouchedFields({
        name: true,
        phone: true,
        email: true,
        headcount: true,
        startTime: true,
        endTime: true,
        customData: Object.fromEntries(
          (settings.guestForm?.customSelects || [])
            .filter(isCustomSelectEnabled)
            .map((field: CustomSelectField) => [field.id, true])
        ),
      });
      focusFirstInvalidField(nextErrors);
      return;
    }
    if (!graceAcknowledged) {
      event.preventDefault();
      setGraceAcceptanceError(true);
      graceCheckboxRef.current?.focus();
      return;
    }
    if (!isFormValid) { event.preventDefault(); return; }
    onSubmit(event);
  };

  const handleCustomFieldChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      customData: { ...prev.customData, [name]: value },
    }));
    const field = settings.guestForm?.customSelects?.find((item: CustomSelectField) => item.id === name);
    if (field && touchedFields.customData[name]) {
      setFormErrors(prev => ({
        ...prev,
        customData: {
          ...prev.customData,
          [name]: validateCustomField(field, value),
        },
      }));
    }
  };

  const handleCustomFieldBlur = (field: CustomSelectField) => {
    setTouchedFields(prev => ({
      ...prev,
      customData: { ...prev.customData, [field.id]: true },
    }));
    setFormErrors(prev => ({
      ...prev,
      customData: {
        ...prev.customData,
        [field.id]: validateCustomField(field, formData.customData[field.id] || ''),
      },
    }));
  };

  const currentAvailabilitySelectionKey = selectedDate
    ? [
        toDateKey(selectedDate),
        formData.startTime || '',
        formData.endTime || '',
        String(formData.headcount ?? ''),
      ].join('|')
    : null;
  const hasCurrentAuthoritativeResult =
    authoritativeAvailability.status === 'ready'
    && authoritativeAvailability.selectionKey === currentAvailabilitySelectionKey;
  const availabilityBlocksSubmit =
    hasCurrentAuthoritativeResult
    && authoritativeAvailability.response.availabilityResult !== 'available';
  const effectiveEndTime =
    formData.endTime
    || addMinutesToTime(formData.startTime || '', BOOKING_FLOW_AVAILABILITY_DEFAULT_DURATION_MINUTES)
    || '';
  const currentIntervalClosed = Boolean(
    selectedDate
    && formData.startTime
    && effectiveEndTime
    && isReservationTimeClosed({
      dateKey: toDateKey(selectedDate),
      startTime: formData.startTime,
      endTime: effectiveEndTime,
      bookingClosures: settings.bookingClosures,
    })
  );

  const isFormValid = useMemo(() => {
    return (
      isReservationDetailsFormComplete(formData, settings) &&
      !validateField('headcount', String(formData.headcount ?? '')) &&
      !validateField('startTime', formData.startTime || '') &&
      !timeWindowOverLimit &&
      !selectedTimeLimitReason &&
      !selectedGroupReason &&
      !currentIntervalClosed &&
      !availabilityBlocksSubmit
    );
  }, [
    availabilityBlocksSubmit,
    selectedTimeLimitReason,
    selectedGroupReason,
    currentIntervalClosed,
    formData,
    settings,
    t,
    timeWindowOverLimit,
  ]);

  const secondaryButtonClass =
    buttonClasses?.secondary ||
    `font-bold py-2 px-4 ${themeProps.radiusClass} ${themeProps.shadowClass}`;
  const primaryButtonClass =
    buttonClasses?.primary ||
    `font-bold py-2 px-6 ${themeProps.radiusClass} ${themeProps.shadowClass} disabled:opacity-50 disabled:cursor-not-allowed text-lg`;

  const surfaceFill =
    themeProps.uiTheme === 'minimal_glass'
      ? toRgba(themeProps.colors.surface, 0.68)
      : themeProps.colors.surface;
  const surfaceBorder =
    themeProps.uiTheme === 'minimal_glass'
      ? toRgba(themeProps.colors.surface, 0.78)
      : themeProps.colors.surface;

  const availabilityStateForMessage =
    authoritativeAvailability.status === 'ready' && !hasCurrentAuthoritativeResult
      ? ({ status: 'idle' } as AuthoritativeAvailabilityState)
      : authoritativeAvailability;
  const authoritativeAvailabilityMessage = currentIntervalClosed
    ? (locale === 'hu' ? 'Ez az idősáv le van zárva.' : 'This time slot is closed.')
    : availabilityStateForMessage.status === 'loading'
    ? (locale === 'hu' ? 'Elérhetőség ellenőrzése…' : 'Checking availability…')
    : availabilityStateForMessage.status === 'error'
    ? (locale === 'hu' ? 'Az elérhetőség most nem ellenőrizhető. A végső ellenőrzés küldéskor megtörténik.' : 'Availability cannot be checked right now. A final check will run on submit.')
    : availabilityStateForMessage.status === 'ready'
    ? availabilityStateForMessage.response.availabilityResult === 'available'
      ? (locale === 'hu' ? 'A kiválasztott időpont elérhető.' : 'The requested time is available.')
      : availabilityStateForMessage.response.availabilityResult === 'capacity_full'
      ? (locale === 'hu' ? 'A kiválasztott időpont kapacitása megtelt.' : 'The requested time is at capacity.')
      : availabilityStateForMessage.response.availabilityResult === 'outside_bookable_window'
      ? (locale === 'hu' ? 'A kiválasztott időpont a foglalási időablakon kívül esik.' : 'The requested time is outside the booking window.')
      : (locale === 'hu' ? 'A kiválasztott időpont nem elérhető.' : 'The requested time is unavailable.')
    : null;

  // Fast shortcuts are filtered against the full default duration and local closures.
  // The selected shortcut still receives an authoritative server check before submission.
  const suggestedStartTimes = useMemo(() => {
    const windowStart = settings.bookableWindow?.from || '11:00';
    const windowEnd = settings.bookableWindow?.to || '23:00';
    const commonTimes = settings.suggestedStartTimesEnabled === true ? (settings.suggestedStartTimes || []).filter((time): time is string => typeof time === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(time)) : [];
    const dateKey = selectedDate ? toDateKey(selectedDate) : null;

    return commonTimes.filter(time => {
      const endTime = addMinutesToTime(time, BOOKING_FLOW_AVAILABILITY_DEFAULT_DURATION_MINUTES);
      if (!endTime || time < windowStart || endTime > windowEnd) return false;
      return !dateKey || !isReservationTimeClosed({
        dateKey,
        startTime: time,
        endTime,
        bookingClosures: settings.bookingClosures,
      });
    });
  }, [selectedDate, settings.bookableWindow, settings.bookingClosures, settings.suggestedStartTimesEnabled, settings.suggestedStartTimes]);

  const handleSuggestedStartTime = (time: string) => {
    setFormData((prev: any) => ({ ...prev, startTime: time }));
    onTimeSelected(time, 'suggested');
    setTouchedFields(prev => ({ ...prev, startTime: true }));
    setFormErrors(prev => ({
      ...prev,
      startTime: validateField('startTime', time),
    }));
  };

  const reviewSummaryDate = selectedDate
    ? selectedDate.toLocaleDateString(locale, { month: 'short', day: 'numeric' })
    : locale === 'hu' ? 'Válasszon dátumot' : 'Choose a date';
  const reviewSummaryGuests = locale === 'hu'
    ? `${formData.headcount || 1} fő`
    : `${formData.headcount || 1} guests`;

  const fieldErrorItems = [
    { key: 'name', label: t.name, message: formErrors.name },
    { key: 'phone', label: t.phone, message: formErrors.phone },
    { key: 'email', label: t.email, message: formErrors.email },
    { key: 'headcount', label: t.headcount, message: formErrors.headcount },
    { key: 'startTime', label: t.startTime, message: formErrors.startTime },
    { key: 'endTime', label: t.endTime, message: formErrors.endTime },
    ...(settings.guestForm?.customSelects || [])
      .filter(isCustomSelectEnabled)
      .map((field: CustomSelectField) => ({
        key: `custom-${field.id}`,
        label: resolvePublicLabel(field, locale),
        message: formErrors.customData[field.id],
      })),
  ].filter((item) => item.message);

  const showRetryAction = error ? isRetryableReservationError(error.kind) : false;
  const showDebugId = Boolean(error?.debugId && shouldShowReservationErrorDebugId(error.kind));
  const errorRecoveryGuidance = useMemo(() => {
    if (!error) return null;
    const copy: Record<Locale, Record<string, { title: string; actions: string[]; reassurance: string }>> = {
      hu: {
        capacity: {
          title: 'Mit tehet most?',
          actions: ['Próbáljon másik érkezési időt.', 'Válasszon másik napot.', 'Ha lehetséges, csökkentse a létszámot.'],
          reassurance: 'A megadott adatok megmaradtak; módosítás után újra elküldheti a kérést.',
        },
        booking_window: {
          title: 'Mit tehet most?',
          actions: ['Ellenőrizze, hogy a kezdés és a befejezés belefér-e a foglalási időablakba.', 'Válassz másik érkezési időt.'],
          reassurance: 'A mezőkben megadott adatok nem vesztek el.',
        },
        validation: {
          title: 'Mit tehet most?',
          actions: ['Ellenőrizze a kiemelt mezőket.', 'Javítás után próbálja újra elküldeni.'],
          reassurance: 'A már megadott adatok megmaradtak az űrlapon.',
        },
        retryable: {
          title: 'Biztonságosan újrapróbálható',
          actions: ['Ellenőrizze a kapcsolatot, majd próbálja újra.', 'Ha a hiba megmarad, várjon egy kicsit, majd küldje el ismét.'],
          reassurance: 'A megadott adatok megmaradtak. Ha az előző küldés visszaigazolás nélkül sikertelen volt, újra megpróbálhatja.',
        },
        generic: {
          title: 'Mit tehet most?',
          actions: ['Ellenőrizze az adatokat.', 'Próbálja újra a küldést.'],
          reassurance: 'Az űrlap adatai megmaradtak.',
        },
      },
      en: {
        capacity: {
          title: 'What you can do next',
          actions: ['Try another arrival time.', 'Choose another date.', 'Reduce the party size if possible.'],
          reassurance: 'Your entered details are preserved; adjust the request and send it again.',
        },
        booking_window: {
          title: 'What you can do next',
          actions: ['Check that start and end time fit inside the booking window.', 'Choose another arrival time.'],
          reassurance: 'Nothing you entered has been lost.',
        },
        validation: {
          title: 'What you can do next',
          actions: ['Review the highlighted fields.', 'Send again after correcting the details.'],
          reassurance: 'Your completed fields are still preserved in the form.',
        },
        retryable: {
          title: 'Safe to retry',
          actions: ['Check your connection and try again.', 'If the issue continues, wait a moment and send again.'],
          reassurance: 'Your details are preserved. If the previous send failed before confirmation, you can try again.',
        },
        generic: {
          title: 'What you can do next',
          actions: ['Review the details.', 'Try sending the request again.'],
          reassurance: 'Nothing has been lost; your form details are still here.',
        },
      },
    };
    const guidanceKey = error.kind === 'capacity'
      ? 'capacity'
      : error.kind === 'booking_window'
      ? 'booking_window'
      : ['validation', 'required_field', 'invalid_email', 'invalid_phone', 'invalid_headcount'].includes(error.kind)
      ? 'validation'
      : isRetryableReservationError(error.kind)
      ? 'retryable'
      : 'generic';
    return copy[locale][guidanceKey];
  }, [error, locale]);

  useEffect(() => {
    if (error && errorBannerRef.current) {
      errorBannerRef.current.focus();
    }
  }, [error]);

  return (
    <div
      className={`p-6 ${themeProps.radiusClass} ${themeProps.shadowClass}`}
      style={{
        backgroundColor: surfaceFill,
        color: themeProps.colors.textPrimary,
        border: `1px solid ${surfaceBorder}`,
      }}
    >
      <h2 className="text-2xl font-semibold text-[var(--color-text-primary)] mb-3">
        {t.step2Title}
      </h2>
      {error && (
        <div
          ref={errorBannerRef}
          role="alert"
          aria-live="assertive"
          tabIndex={-1}
          className="p-4 mb-4 bg-red-100 text-red-800 font-semibold rounded-lg text-sm leading-relaxed break-words border border-red-200 shadow-sm flex flex-col gap-3"
        >
          <div>
            <p className="text-base font-bold">{error.userTitle}</p>
            <p className="mt-1">{error.userMessage}</p>
          </div>
          {showDebugId && (
            <p className="text-xs font-medium">
              {locale === 'hu' ? 'Hiba azonosító' : 'Error ID'}: {error.debugId}
            </p>
          )}
          {showRetryAction && (
            <button
              type="button"
              onClick={onRetry}
              className={`${buttonClasses.primary} ${themeProps.radiusClass} text-sm`}
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              {locale === 'hu' ? 'Próbálja újra' : 'Try again'}
            </button>
          )}
          {errorRecoveryGuidance && (
            <div className="rounded-lg border border-red-200 bg-white/70 p-3 text-sm font-medium text-red-900">
              <p className="font-bold">{errorRecoveryGuidance.title}</p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                {errorRecoveryGuidance.actions.map(action => (
                  <li key={action}>{action}</li>
                ))}
              </ul>
              <p className="mt-2 text-xs leading-relaxed">{errorRecoveryGuidance.reassurance}</p>
            </div>
          )}
        </div>
      )}
      {fieldErrorItems.length > 0 && (
        <div
          role="alert"
          className={`mb-4 p-3 ${themeProps.radiusClass}`}
          style={{
            backgroundColor: `${themeProps.colors.danger}15`,
            color: themeProps.colors.textPrimary,
            border: `1px solid ${themeProps.colors.danger}40`,
          }}
        >
          <p className="text-sm font-semibold">
            {locale === 'hu' ? 'Ellenőrizze az alábbi mezőket:' : 'Please review:'}
          </p>
          <ul className="mt-2 space-y-1 text-xs list-disc list-inside">
            {fieldErrorItems.map((item) => (
              <li key={item.key}>
                {item.label}: {item.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      {groupSummary && <p className="rounded-lg border p-4 text-[15px] font-bold leading-relaxed">{groupSummary}</p>}
      {selectedTimeLimitReason && selectedTimeLimitReason !== 'SAME_DAY_DISABLED' && selectedTimeLimitReason !== 'SAME_DAY_CUTOFF_PASSED' && (
        <p role="status" className="rounded-lg border p-4 text-sm">
          {reservationTimeLimitMessage(selectedTimeLimitReason, locale)}
        </p>
      )}
      <form id="reservation-details-form" onSubmit={handleValidatedSubmit} className="space-y-5 scroll-pb-36" noValidate>
        {(settings?.businessRules?.terms?.[locale] || settings?.businessRules?.terms?.hu) && <p className="whitespace-pre-wrap rounded-lg border p-4">{settings?.businessRules?.terms?.[locale] || settings?.businessRules?.terms?.hu}</p>}
          <label className="sr-only" htmlFor="reservation-selected-date">{t.date}</label>
          <input
            id="reservation-selected-date"
            type="button"
            onClick={onBack}
            aria-label={locale === 'hu' ? 'Kiválasztott dátum' : 'Selected date'}
            value={selectedDate.toLocaleDateString(locale, {
              weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
          className={`w-full cursor-pointer p-3 border ${themeProps.radiusClass} text-center font-semibold focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]`}
            style={{
              ...inputTextStyle,
              backgroundColor: themeProps.colors.surface,
              borderColor: themeProps.colors.surface,
            }}
          />
        <section className="space-y-3 rounded-2xl border border-black/5 bg-white/20 p-4" aria-labelledby="reservation-visit-heading">
          <div>
            <h3 id="reservation-visit-heading" className="text-sm font-bold uppercase tracking-wide" style={{ color: themeProps.colors.textSecondary }}>
              {locale === 'hu' ? 'Látogatás részletei' : 'Visit details'}
            </h3>
            <p className="mt-1 text-xs leading-relaxed" style={{ color: themeProps.colors.textSecondary }}>
              {locale === 'hu' ? 'Először adja meg a létszámot, majd az érkezési időt a kapacitás ellenőrzéséhez.' : 'Please enter the party size first, then the arrival time to check availability.'}
            </p>
          </div>
          {suggestedStartTimes.length > 0 && (
            <div className="space-y-2" aria-label={locale === 'hu' ? 'Javasolt érkezési idők' : 'Suggested arrival times'}>
              <p className="text-xs" style={{ color: themeProps.colors.textSecondary }}>
                {locale === 'hu' ? 'Választhat egy javasolt időpontot, vagy megadhatja a kívánt időpontot.' : 'Select a suggested time, or enter your preferred time.'}
              </p>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold" style={{ color: themeProps.colors.textPrimary }}>
                  {locale === 'hu' ? 'Javasolt időpontok' : 'Suggested times'}
                </p>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {suggestedStartTimes.map(time => {
                  const isSelected = formData.startTime === time;
                  return (
                    <button
                      key={time}
                      type="button"
                      onClick={() => handleSuggestedStartTime(time)}
                      className={`min-h-10 whitespace-nowrap border px-3 py-2 text-sm font-semibold transition motion-reduce:transition-none ${themeProps.radiusClass} focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]`}
                      aria-pressed={isSelected}
                      style={{
                        backgroundColor: isSelected ? themeProps.colors.primary : toRgba(themeProps.colors.surface, 0.82),
                        borderColor: isSelected ? themeProps.colors.primary : toRgba(themeProps.colors.textSecondary, 0.18),
                        color: isSelected ? '#fff' : themeProps.colors.textPrimary,
                      }}
                    >
                      {time}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs leading-relaxed" style={{ color: themeProps.colors.textSecondary }}>
                {locale === 'hu'
                  ? 'Választhat egy javasolt időt, vagy megadhat más időpontot kézzel.'
                  : 'Choose a suggested time or enter another time manually.'}
              </p>
            </div>
          )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium mb-1">{t.headcount}<span className="text-red-600" aria-hidden="true"> *</span></label>
            <input
              ref={headcountInputRef}
              type="number"
              name="headcount"
              value={formData.headcount}
              onChange={handleStandardChange}
              onBlur={handleStandardBlur}
              aria-invalid={Boolean(formErrors.headcount)}
              aria-describedby={formErrors.headcount ? getFieldErrorId('headcount') : undefined}
              min="1"
                className={`${themeProps.radiusClass} w-full p-3 border placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] ${formErrors.headcount ? 'border-red-500' : ''}`}
              style={inputTextStyle}
              inputMode="numeric"
              required
            />
            {formErrors.headcount && (
              <p id={getFieldErrorId('headcount')} className="text-red-500 text-xs mt-1">{formErrors.headcount}</p>
            )}
            <p className="mt-2 text-xs" style={{ color: themeProps.colors.textSecondary }}>
              {locale === 'hu' ? <>Az asztalt a foglalás kezdetétől számított <strong>{graceMinutes} percig</strong> tartjuk fenn.</> : <>We hold your table for <strong>{graceMinutes} minutes</strong> from the reservation start time.</>}
            </p>
            <ReservationGroupNotice settings={settings} headcount={Number(formData.headcount)} locale={locale} date={toDateKey(selectedDate)} time={formData.startTime} forceContact={error?.canonicalError.metadata.backendError === 'group_contact_required'} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t.startTime}<span className="text-red-600" aria-hidden="true"> *</span></label>
            <input
              ref={startTimeInputRef}
              type="time"
              name="startTime"
              value={formData.startTime}
              onChange={handleStandardChange}
              onBlur={handleStandardBlur}
              aria-invalid={Boolean(formErrors.startTime)}
              aria-describedby={formErrors.startTime ? getFieldErrorId('startTime') : undefined}
                className={`${themeProps.radiusClass} w-full p-3 border placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] ${formErrors.startTime ? 'border-red-500' : ''}`}
              style={inputTextStyle}
              required
              min={settings.bookableWindow?.from}
              max={settings.bookableWindow?.to}
            />
            {formErrors.startTime && (
              <p id={getFieldErrorId('startTime')} className="text-red-500 text-xs mt-1">{formErrors.startTime}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t.endTime} <span className="font-normal" style={{ color: themeProps.colors.textSecondary }}>({locale === 'hu' ? 'opcionális' : 'optional'})</span></label>
            <input
              ref={endTimeInputRef}
              type="time"
              name="endTime"
              value={formData.endTime}
              onChange={handleStandardChange}
              onBlur={handleStandardBlur}
              aria-invalid={Boolean(formErrors.endTime)}
              aria-describedby={formErrors.endTime ? getFieldErrorId('endTime') : undefined}
                className={`${themeProps.radiusClass} w-full p-3 border placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] ${formErrors.endTime ? 'border-red-500' : ''}`}
              style={inputTextStyle}
              min={formData.startTime}
            />
            <p className="mt-1 text-xs leading-relaxed" style={{ color: themeProps.colors.textSecondary }}>
              {t.endTimeHelper}
            </p>
            {formErrors.endTime && (
              <p id={getFieldErrorId('endTime')} className="text-red-500 text-xs mt-1">{formErrors.endTime}</p>
            )}
            {authoritativeAvailabilityMessage && (
              <p
                data-authoritative-availability-result
                role="status"
                className={`mt-2 text-xs ${
                  currentIntervalClosed
                  || (hasCurrentAuthoritativeResult
                  && authoritativeAvailability.status === 'ready'
                  && authoritativeAvailability.response.availabilityResult !== 'available')
                    ? 'text-red-600'
                    : hasCurrentAuthoritativeResult && authoritativeAvailability.status === 'ready' && authoritativeAvailability.response.availabilityResult === 'available'
                    ? 'font-semibold text-green-600'
                    : 'text-[var(--color-text-secondary)]'
                }`}
              >
                {authoritativeAvailabilityMessage}
              </p>
            )}
          </div>
        </div>
        </section>
        <section className="space-y-3 rounded-2xl border border-black/5 bg-white/20 p-4" aria-labelledby="reservation-contact-heading">
          <div>
            <h3 id="reservation-contact-heading" className="text-sm font-bold uppercase tracking-wide" style={{ color: themeProps.colors.textSecondary }}>
              {locale === 'hu' ? 'Elérhetőségek' : 'Contact details'}
            </h3>
            <p className="mt-1 text-xs leading-relaxed" style={{ color: themeProps.colors.textSecondary }}>
              {locale === 'hu'
                ? 'Az elérhetőségeket csak a foglalás egyeztetéséhez és a visszaigazoláshoz használjuk. A visszaigazoló e-mailben kezelési linket is küldünk.'
                : 'We use your contact details only to coordinate and confirm this reservation. Your confirmation email includes a private management link.'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium mb-1">{t.name}<span className="text-red-600" aria-hidden="true"> *</span></label>
            <input
              ref={nameInputRef}
              type="text"
              name="name"
              value={formData.name}
              onChange={handleStandardChange}
              onBlur={handleStandardBlur}
              aria-invalid={Boolean(formErrors.name)}
              aria-describedby={formErrors.name ? getFieldErrorId('name') : undefined}
              className={`${themeProps.radiusClass} w-full p-3 border placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] ${formErrors.name ? 'border-red-500' : ''}`}
              style={inputTextStyle}
              autoComplete="name"
              required
            />
            {formErrors.name && (
              <p id={getFieldErrorId('name')} className="text-red-500 text-xs mt-1">{formErrors.name}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t.phone}<span className="text-red-600" aria-hidden="true"> *</span></label>
            <input
              ref={phoneInputRef}
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleStandardChange}
              onBlur={handleStandardBlur}
              aria-invalid={Boolean(formErrors.phone)}
              aria-describedby={formErrors.phone ? getFieldErrorId('phone') : undefined}
              placeholder={t.phonePlaceholder}
              className={`${themeProps.radiusClass} w-full p-3 border placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] ${formErrors.phone ? 'border-red-500' : ''}`}
              style={inputTextStyle}
              autoComplete="tel"
              inputMode="tel"
              required
            />
            {formErrors.phone && (
              <p id={getFieldErrorId('phone')} className="text-red-500 text-xs mt-1">{formErrors.phone}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t.email}<span className="text-red-600" aria-hidden="true"> *</span></label>
            <input
              ref={emailInputRef}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleStandardChange}
              onBlur={handleStandardBlur}
              aria-invalid={Boolean(formErrors.email)}
              aria-describedby={formErrors.email ? getFieldErrorId('email') : undefined}
                className={`${themeProps.radiusClass} w-full p-3 border placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] ${formErrors.email ? 'border-red-500' : ''}`}
              style={inputTextStyle}
              autoComplete="email"
              inputMode="email"
              required
            />
            {formErrors.email && (
              <p id={getFieldErrorId('email')} className="text-red-500 text-xs mt-1">{formErrors.email}</p>
            )}
          </div>
          </div>
        </section>
        <section className="grid grid-cols-1 gap-5 rounded-2xl border border-black/5 bg-white/20 p-4" aria-labelledby="reservation-preferences-heading">
          <h3 id="reservation-preferences-heading" className="text-sm font-bold uppercase tracking-wide" style={{ color: themeProps.colors.textSecondary }}>
            {locale === 'hu' ? 'Preferenciák' : 'Preferences'}
          </h3>
          <div>
            <label className="block text-sm font-medium mb-1">{t.seatingPreferenceLabel}</label>
            <div
              className={`grid grid-cols-2 gap-2 ${themeProps.radiusClass}`}
              role="radiogroup"
              aria-label={t.seatingPreferenceLabel}
            >
              {(
                settings.guestForm?.seatingPreferenceOptions
                  ?.filter(option => option.enabled !== false)
                  .map(option => ({ value: option.value as SeatingPreference, label: resolvePublicLabel(option, locale) })) ||
                DEFAULT_SEATING_OPTIONS.map(option => ({ value: option.value, label: t[option.labelKey] }))
              ).map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center justify-center px-3 py-2 border text-sm font-medium cursor-pointer transition-colors motion-reduce:transition-none ${themeProps.radiusClass} ${
                    formData.seatingPreference === option.value
                      ? 'bg-[var(--color-primary)] text-white border-transparent'
                      : ''
                  }`}
                  style={{
                    borderColor:
                      formData.seatingPreference === option.value
                        ? 'transparent'
                        : themeProps.colors.surface,
                    backgroundColor:
                      formData.seatingPreference === option.value
                        ? 'var(--color-primary)'
                        : themeProps.colors.surface,
                    color:
                      formData.seatingPreference === option.value
                        ? '#fff'
                        : themeProps.colors.textPrimary,
                  }}
                >
                  <input
                    type="radio"
                    name="seatingPreference"
                    value={option.value}
                    checked={formData.seatingPreference === option.value}
                    onChange={handleStandardChange}
                    className="sr-only"
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>
        {settings.guestForm?.customSelects?.filter(isCustomSelectEnabled).map((field: CustomSelectField) => (
          <div key={field.id}>
            <label className="block text-sm font-medium mb-1">{resolvePublicLabel(field, locale)}{isCustomSelectRequired(field) ? <span className="text-red-600" aria-hidden="true"> *</span> : <span className="font-normal" style={{ color: themeProps.colors.textSecondary }}> ({locale === 'hu' ? 'opcionális' : 'optional'})</span>}</label>
            <select
              ref={(element) => {
                customSelectRefs.current[field.id] = element;
              }}
              name={field.id}
              value={formData.customData[field.id] || ''}
              onChange={handleCustomFieldChange}
              onBlur={() => handleCustomFieldBlur(field)}
              aria-invalid={Boolean(formErrors.customData[field.id])}
              aria-describedby={formErrors.customData[field.id] ? getCustomFieldErrorId(field.id) : undefined}
              className={`${themeProps.radiusClass} w-full p-3 border placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] ${formErrors.customData[field.id] ? 'border-red-500' : ''}`}
              style={inputTextStyle}
              required={isCustomSelectRequired(field)}
            >
              <option value="" disabled>
                {locale === 'hu' ? 'Válasszon...' : 'Select...'}
              </option>
              {field.options.map((o: string) => (
                <option key={o} value={o}>
                  {resolveCustomSelectOptionLabel(field, o, locale)}
                </option>
              ))}
            </select>
            {formErrors.customData[field.id] && (
              <p id={getCustomFieldErrorId(field.id)} className="text-red-500 text-xs mt-1">
                {formErrors.customData[field.id]}
              </p>
            )}
          </div>
        ))}
        </section>
        <section className="space-y-3 rounded-2xl border border-black/5 bg-white/20 p-4" aria-labelledby="reservation-review-heading">
          <div>
            <h3 id="reservation-review-heading" className="text-sm font-bold uppercase tracking-wide" style={{ color: themeProps.colors.textSecondary }}>
              {locale === 'hu' ? 'Ellenőrzés és küldés' : 'Review and submit'}
            </h3>
            <p className="mt-1 hidden text-xs leading-relaxed md:block" style={{ color: themeProps.colors.textSecondary }}>
              {locale === 'hu'
                ? 'Ellenőrizze az adatokat, majd küldje el a foglalási kérelmet. Hiba esetén az adatai megmaradnak, és biztonságosan újrapróbálhatja.'
                : 'Review your details, then send the reservation request. If something fails, your details stay in place and you can safely retry.'}
            </p>
          </div>
          <div
            className={`${themeProps.radiusClass} border p-3 text-sm font-semibold`}
            style={{
              backgroundColor: toRgba(themeProps.colors.surface, 0.72),
              borderColor: toRgba(themeProps.colors.textSecondary, 0.16),
              color: themeProps.colors.textPrimary,
            }}
          >
            <div className="grid grid-cols-1 gap-x-6 gap-y-1 font-normal md:grid-cols-2 [&>p]:min-w-0 [&>p]:break-words">
              <p><strong>{t.date}:</strong> {reviewSummaryDate}</p>
              <p><strong>{t.startTime}:</strong> {formData.startTime || '–'}</p>
              <p><strong>{t.endTime}:</strong> {formData.endTime || '–'}</p>
              <p><strong>{t.headcount}:</strong> {reviewSummaryGuests}</p>
              <p><strong>{t.name}:</strong> {formData.name}</p>
              <p><strong>{t.email}:</strong> {formData.email}</p>
              <p><strong>{t.phone}:</strong> {formData.phone}</p>
              {(settings.guestForm?.customSelects || []).filter(isCustomSelectEnabled).map(field => (
                <p key={field.id}><strong>{resolvePublicLabel(field, locale)}:</strong> {formData.customData[field.id] || '–'}</p>
              ))}
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <label className="flex items-start gap-2">
              <input ref={graceCheckboxRef} type="checkbox" className="mt-1" checked={graceAcknowledged} aria-invalid={graceAcceptanceError} aria-describedby={graceAcceptanceError ? 'reservation-grace-error' : undefined} onChange={event => { setGraceAcknowledged(event.target.checked); setGraceAcceptanceError(false); }} required />
              <span>{locale === 'hu'
                ? `Tudomásul veszem, hogy az asztalt a foglalás kezdetétől számított ${graceMinutes} percig tartják fenn. Ezt követően az ültetés a szabad kapacitás függvényében lehetséges, és nem garantált.`
                : `I acknowledge that my table is held for ${graceMinutes} minutes from the reservation start time. Seating after this period depends on availability and is not guaranteed.`}<span className="text-red-600" aria-hidden="true"> *</span></span>
            </label>
            {graceAcceptanceError && <p id="reservation-grace-error" role="alert" className="text-red-600">{locale === 'hu' ? 'A foglalás elküldéséhez a tájékoztatás elfogadása kötelező.' : 'Please accept the required acknowledgement to submit your reservation.'}</p>}
            <p style={{ color: themeProps.colors.textSecondary }}>{locale === 'hu'
              ? 'Késés vagy létszámváltozás esetén kérjük, értesítsen minket telefonon.'
              : 'Please call us if you are running late or your party size changes.'}</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={onBack}
              className={secondaryButtonClass}
            >
              {t.back}
            </button>
            <div className="text-left sm:text-right">
              {!isFormValid && (
                <p id="reservation-submit-helper" className="mb-2 text-xs" style={{ color: themeProps.colors.textSecondary }}>
                  {availabilityBlocksSubmit || currentIntervalClosed || timeWindowOverLimit
                    ? (locale === 'hu' ? 'Válassz egy elérhető érkezési időt.' : 'Choose an available arrival time.')
                    : (locale === 'hu' ? 'Töltsd ki a kötelező mezőket, és ellenőrizd az érkezési időt a küldéshez.' : 'Complete the required fields and check the arrival time to send the reservation.')}
                </p>
              )}
              <button
                type="submit"
                aria-describedby={!isFormValid ? 'reservation-submit-helper' : undefined}
                disabled={isSubmitting}
                className={primaryButtonClass}
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                {isSubmitting ? t.submitting : t.next}
              </button>
            </div>
          </div>
        </section>
      </form>
    </div>
  );
};

interface Step3ConfirmationProps {
  onReset: () => void;
  theme: ReservationThemeTokens;
  themeProps: any;
  t: any;
  submittedData: any;
  unit: Unit;
  locale: Locale;
  settings: ReservationSetting;
  buttonClasses: { primary: string; secondary: string };
}

const Step3Confirmation: React.FC<Step3ConfirmationProps> = ({
  onReset,
  theme,
  themeProps,
  t,
  submittedData,
  unit,
  locale,
  settings,
  buttonClasses,
}) => {
  const [copied, setCopied] = useState(false);

  const { googleLink, icsLink, manageLink } = useMemo(() => {
    if (!submittedData)
      return { googleLink: '#', icsLink: '#', manageLink: '#' };

    const { startTime, endTime, name, referenceCode, manageToken } = submittedData;
    const startDate = startTime.toDate();
    const endDate = endTime.toDate();

    const formatDate = (date: Date) =>
      date.toISOString().replace(/-|:|\.\d\d\d/g, '');

    const gCalParams = new URLSearchParams({
      action: 'TEMPLATE',
      text: `${unit.name} - ${t.title}`,
      dates: `${formatDate(startDate)}/${formatDate(endDate)}`,
      details: `${t.name}: ${name}\n${t.referenceCode}: ${referenceCode}`,
      location: unit.name,
    });
    const gLink = `https://www.google.com/calendar/render?${gCalParams.toString()}`;

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'BEGIN:VEVENT',
      `DTSTART:${formatDate(startDate)}`,
      `DTEND:${formatDate(endDate)}`,
      `SUMMARY:${unit.name} - ${t.title}`,
      `DESCRIPTION:${t.name}: ${name}\\n${t.referenceCode}: ${referenceCode}`,
      `LOCATION:${unit.name}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');
    const iLink = `data:text/calendar;charset=utf8,${encodeURIComponent(
      icsContent
    )}`;

    if (!manageToken) {
      return { googleLink: gLink, icsLink: iLink, manageLink: '#' };
    }

    const mLinkParams = new URLSearchParams({
      reservationId: referenceCode,
      unitId: unit.id,
      token: manageToken,
    });
    const mLink = `${window.location.origin}/manage?${mLinkParams.toString()}`;

    return { googleLink: gLink, icsLink: iLink, manageLink: mLink };
  }, [submittedData, unit.name, t]);

  const handleCopy = () => {
    if (manageLink === '#') return;
    navigator.clipboard.writeText(manageLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const maskPhone = (phoneE164: string): string => {
    if (!phoneE164 || phoneE164.length < 10) return phoneE164;
    const last4 = phoneE164.slice(-4);
    return phoneE164.slice(0, -7) + '••• •' + last4;
  };

  const isAutoConfirm = settings.reservationMode === 'auto';
  const titleText = isAutoConfirm ? t.step3TitleConfirmed : t.step3Title;
  const bodyText = isAutoConfirm ? t.step3BodyConfirmed : t.step3Body;
  const nextStepsText =
    locale === 'hu'
      ? 'Mi történik ezután? A foglalás részleteit e-mailben küldjük, és a linken bármikor módosíthatod vagy lemondhatod.'
      : 'What happens next? We will email your reservation details, and you can modify or cancel anytime via the link.';
  const primaryButtonClass =
    buttonClasses?.primary ||
    `text-white font-bold py-3 px-6 ${themeProps.radiusClass}`;

  const confirmationSurface =
    themeProps.uiTheme === 'minimal_glass'
      ? toRgba(themeProps.colors.surface, 0.9)
      : themeProps.colors.surface;

  return (
    <div
      className={`p-6 md:p-10 ${themeProps.radiusClass} ${themeProps.shadowClass} text-center space-y-5`}
      style={{
        backgroundColor: confirmationSurface,
        color: themeProps.colors.textPrimary,
        border: `1px solid ${themeProps.colors.surface}`,
      }}
    >
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full text-3xl font-bold" style={{ backgroundColor: toRgba(themeProps.colors.success, 0.12), color: 'var(--color-success)' }} aria-hidden="true">
        ✓
      </div>
      <div className="space-y-2">
        <div className="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide" style={{ backgroundColor: toRgba(themeProps.colors.success, 0.12), color: 'var(--color-success)' }}>
          {isAutoConfirm ? (locale === 'hu' ? 'Megerősítve' : 'Confirmed') : (locale === 'hu' ? 'Kérés elküldve' : 'Request sent')}
        </div>
        <h2 className="text-2xl md:text-3xl font-bold" style={{ color: 'var(--color-success)' }}>
          {titleText}
        </h2>
      </div>
      <p className="text-[var(--color-text-primary)] mt-4 leading-relaxed">{bodyText}</p>
      <div className="grid gap-3 text-left md:grid-cols-3">
        {[
          locale === 'hu' ? 'A részleteket e-mailben elküldjük.' : 'We send the details by email.',
          locale === 'hu' ? 'A kezelési linken módosíthatod vagy lemondhatod.' : 'Use the management link to modify or cancel.',
          locale === 'hu' ? 'Mentsd el a foglalási azonosítót.' : 'Save your reservation reference.',
        ].map(item => (
          <div key={item} className="rounded-xl border p-3 text-sm font-medium" style={{ backgroundColor: toRgba(themeProps.colors.surface, 0.72), borderColor: themeProps.colors.surface, color: themeProps.colors.textPrimary }}>
            {item}
          </div>
        ))}
      </div>
      <p className="text-sm mt-2" style={{ color: themeProps.colors.textSecondary }}>
        {t.emailConfirmationSent}
      </p>
      <p className="text-sm" style={{ color: themeProps.colors.textSecondary }}>
        {nextStepsText}
      </p>

      {!submittedData && (
        <div role="status" className="rounded-xl border p-4 text-left text-sm" style={{ backgroundColor: toRgba(themeProps.colors.surface, 0.72), borderColor: themeProps.colors.surface, color: themeProps.colors.textSecondary }}>
          {locale === 'hu'
            ? 'A visszaigazolási adatok betöltése folyamatban. Ha nem jelennek meg, ellenőrizd az e-mailt vagy indíts új foglalást.'
            : 'Confirmation details are loading. If they do not appear, check your email or start a new reservation.'}
        </div>
      )}

      {submittedData && (
        <div
          className="mt-6 text-left p-4 md:p-5 border rounded-xl shadow-sm space-y-3"
          style={{
            backgroundColor: confirmationSurface,
            color: themeProps.colors.textPrimary,
            borderColor: themeProps.colors.surface,
          }}
        >
          <h3 className="font-bold text-center mb-3 text-lg">{t.step3Details}</h3>
          <p>
            <strong>{t.referenceCode}:</strong>{' '}
            <span
              className={`font-mono text-lg font-bold tracking-wide px-3 py-2 ${themeProps.radiusClass}`}
              style={{
                backgroundColor: themeProps.colors.surface,
                color: themeProps.colors.textPrimary,
              }}
            >
              {submittedData.referenceCode.substring(0, 8).toUpperCase()}
            </span>
          </p>
          <p>
            <strong>{t.name}:</strong> {submittedData.name}
          </p>
          <p>
            <strong>{t.headcount}:</strong> {submittedData.headcount}
          </p>
          <p>
            <strong>{t.date}:</strong>{' '}
            {submittedData.date.toLocaleDateString(locale, {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
          <p>
            <strong>{t.startTime}:</strong>{' '}
            {submittedData.startTime
              .toDate()
              .toLocaleTimeString(locale, {
                hour: '2-digit',
                minute: '2-digit',
              })}
          </p>
          <p>
            <strong>{t.endTime}:</strong>{' '}
            {submittedData.endTime
              .toDate()
              .toLocaleTimeString(locale, {
                hour: '2-digit',
                minute: '2-digit',
              })}
          </p>
          <p>
            <strong>{t.email}:</strong> {submittedData.contact.email}
          </p>
          <p>
            <strong>{t.phone}:</strong>{' '}
            {submittedData.contact?.phoneE164
              ? maskPhone(submittedData.contact.phoneE164)
              : 'N/A'}
          </p>
          <p>
            <strong>{t.seatingPreferenceLabel}:</strong>{' '}
            {submittedData.seatingPreference && submittedData.seatingPreference !== 'any'
              ? ({
                  bar: t.seatingPreferenceBar,
                  table: t.seatingPreferenceTable,
                  outdoor: t.seatingPreferenceOutdoor,
                } as Record<string, string>)[submittedData.seatingPreference] || t.preferenceNotProvided
              : t.preferenceNotProvided}
          </p>
          {Object.entries(submittedData.customData || {}).map(([key, value]) => {
            const field = settings.guestForm?.customSelects?.find(
              (f) => f.id === key
            );
            if (!field || !value) return null;
            return (
              <p key={key}>
                <strong>{resolvePublicLabel(field, locale)}:</strong>{' '}
                {resolveCustomSelectOptionLabel(field, value as string, locale)}
              </p>
            );
          })}
        </div>
      )}

      <div
        className="mt-6 text-left p-4 md:p-5 rounded-lg border shadow-sm space-y-2"
        style={{
          backgroundColor: confirmationSurface,
          color: themeProps.colors.textPrimary,
          borderColor: themeProps.colors.surface,
        }}
      >
        <h3 className="font-semibold mb-2">{t.manageLinkTitle}</h3>
        <p className="text-sm mb-2" style={{ color: themeProps.colors.textSecondary }}>
          {t.manageLinkBody}
        </p>
        <div
          className={`flex flex-col gap-2 p-2 sm:flex-row sm:items-center ${themeProps.radiusClass} border`}
          style={{
            backgroundColor: themeProps.colors.surface,
            borderColor: themeProps.colors.surface,
          }}
        >
          <input
            type="text"
            value={manageLink === '#' ? '' : manageLink}
            readOnly
            className="w-full bg-transparent text-sm focus:outline-none placeholder:text-gray-600"
            style={inputTextStyle}
            aria-label={t.manageLinkTitle}
          />
          <div className="flex gap-2">
            {manageLink !== '#' && (
              <a
                href={manageLink}
                target="_blank"
                rel="noopener noreferrer"
                className={`${themeProps.radiusClass} font-semibold text-sm px-3 py-1.5 whitespace-nowrap ${theme.shadowClass}`}
                style={{ backgroundColor: themeProps.colors.accent, color: '#fff' }}
              >
                {locale === 'hu' ? 'Megnyitás' : 'Open'}
              </a>
            )}
            <button
              onClick={handleCopy}
              disabled={manageLink === '#'}
              className={`${themeProps.radiusClass} font-semibold text-sm px-3 py-1.5 whitespace-nowrap flex items-center gap-1.5 ${theme.shadowClass}`}
              style={{
                backgroundColor:
                  manageLink === '#'
                    ? themeProps.colors.textSecondary
                    : themeProps.colors.primary,
                color: '#fff',
                opacity: manageLink === '#' ? 0.6 : 1,
              }}
            >
              <CopyIcon className="h-4 w-4" />
              {copied ? t.copied : t.copy}
            </button>
          </div>
        </div>
        {manageLink === '#' && (
          <p className="text-sm text-red-600">{t.invalidManageLink}</p>
        )}
      </div>

      <div className="mt-6 space-y-3">
        <h3 className="font-semibold text-center">{t.addToCalendar}</h3>
        <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
          <a
            href={googleLink}
            target="_blank"
            rel="noopener noreferrer"
            className={`font-semibold py-2 px-4 flex items-center gap-2 ${themeProps.radiusClass}`}
            style={{ backgroundColor: themeProps.colors.primary, color: '#fff' }}
          >
            <CalendarIcon className="h-5 w-5" /> {t.googleCalendar}
          </a>
          <a
            href={icsLink}
            download={`${unit.name}-reservation.ics`}
            className={`font-semibold py-2 px-4 flex items-center gap-2 ${themeProps.radiusClass}`}
            style={{ backgroundColor: themeProps.colors.accent, color: '#fff' }}
          >
            <CalendarIcon className="h-5 w-5" /> {t.otherCalendar}
          </a>
        </div>
      </div>

      <button
        onClick={onReset}
        className={`${primaryButtonClass} mt-8 w-full sm:w-auto`}
        style={{ backgroundColor: themeProps.colors.primary }}
      >
        {t.newBooking}
      </button>
    </div>
  );
};

export default ReservationPage;
